// src/modulos/TUV/TUVReportPage.js

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import ExcelJS from 'exceljs';
import {
    getBlob, ref, uploadBytes, getDownloadURL, deleteObject,
    uploadBytesResumable, listAll
} from 'firebase/storage';
import {
    getFirestore, collection, addDoc, getDocs, deleteDoc, doc,
    query, orderBy, getDoc, updateDoc, setDoc, where
} from 'firebase/firestore';
import { firebaseApp, auth as firebaseAuth, storage as firebaseStorage, db as firebaseDb } from '../../firebaseConfig';
import styles from '../Registers/Registers.module.css';
import PendingEvaluationsList from '../../components/global/PendingEvaluations/PendingEvaluationsList';
import { webhookService } from '../../services/webhookService';
import { HiOutlineDocumentDownload, HiOutlineTrash, HiOutlineCheckCircle, HiOutlineClock } from 'react-icons/hi';
import { getFunctions, httpsCallable } from 'firebase/functions';

// --------------------------------------------------------------
// Firebase initialization
// --------------------------------------------------------------
const storage = firebaseStorage;
const db = firebaseDb;
const auth = firebaseAuth;
const functions = getFunctions(firebaseApp);

// --------------------------------------------------------------
// API Configuration
// --------------------------------------------------------------
const CHECKLIST_API_BASE = 'https://integration.checklistfacil.com.br';
const CHECKLIST_API_KEY = 'XBH0PhGXxSQ1ve3Kaf5PT2s6bLDOYD8o6LbexfJDz5kfVBqcfFsktHobZXkhTyzuJeTiF0EZC76I1EwXV4syCSrEtWw0QeBSjL9uMIqbiepfFFwmO3AMsiHOFJ6kNsn0';

// --------------------------------------------------------------
// Cache e Rate Limiting
// --------------------------------------------------------------
const evaluationCache = new Map();
const requestTimestamps = new Map();
const CACHE_DURATION = 5 * 60 * 1000;
const RATE_LIMIT_INTERVAL = 61000;

const checkRateLimit = (evaluationId) => {
    const lastRequest = requestTimestamps.get(evaluationId);
    if (lastRequest) {
        const timeSinceLastRequest = Date.now() - lastRequest;
        if (timeSinceLastRequest < RATE_LIMIT_INTERVAL) {
            const remainingSeconds = Math.ceil((RATE_LIMIT_INTERVAL - timeSinceLastRequest) / 1000);
            throw new Error(`RATE_LIMIT: Aguarde ${remainingSeconds} segundos antes de buscar esta avaliação novamente.`);
        }
    }
};

const recordRequest = (evaluationId) => {
    requestTimestamps.set(evaluationId, Date.now());
};

const fetchWithRetry = async (url, options, maxRetries = 3, delay = 60000) => {
    for (let i = 0; i < maxRetries; i++) {
        try {
            const response = await fetch(url, options);
            if (response.status === 429) {
                const waitTime = delay * (i + 1);
                await new Promise(resolve => setTimeout(resolve, waitTime));
                continue;
            }
            return response;
        } catch (error) {
            if (i === maxRetries - 1) throw error;
            await new Promise(resolve => setTimeout(resolve, delay * (i + 1)));
        }
    }
    throw new Error('Max retries reached');
};

// --------------------------------------------------------------
// Generic bounded-concurrency mapper
// --------------------------------------------------------------
const mapWithConcurrency = async (items, limit, worker) => {
    const results = new Array(items.length);
    let nextIndex = 0;

    const runners = Array.from(
        { length: Math.min(Math.max(1, limit), items.length) },
        async () => {
            while (true) {
                const current = nextIndex++;
                if (current >= items.length) return;
                results[current] = await worker(items[current], current);
            }
        }
    );

    await Promise.all(runners);
    return results;
};

// --------------------------------------------------------------
// Enhanced Parser
// --------------------------------------------------------------
const parseProductDetailsSimple = (text) => {
    if (!text || text.trim() === '') {
        return [];
    }

    const lines = text.split(/\r?\n/);
    const products = [];

    const isDate = (str) => /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/.test(str);
    const isQuantity = (str) => /^[\d,.]+$/.test(str);

    const extractProductName = (text) => {
        const match = text.match(/^(.+?)\s*\(/);
        return match ? match[1].trim() : text.trim();
    };

    for (const line of lines) {
        const trimmedLine = line.trim();
        if (!trimmedLine) continue;

        const parenMatch = trimmedLine.match(/\(([^)]+)\)/);
        if (!parenMatch) continue;

        const contentInsideParens = parenMatch[1];
        const afterParens = trimmedLine.substring(trimmedLine.indexOf(')') + 1).trim();

        let batches = [];
        if (contentInsideParens.includes(',')) {
            batches = contentInsideParens.split(',').map(b => b.trim()).filter(Boolean);
        } else {
            batches = contentInsideParens.split(/\s+/).filter(Boolean);
        }

        const afterParts = afterParens.split(/\s+/);
        let quantity = '';
        let productionDate = '';
        let expiryDate = '';

        let dateCount = 0;
        for (const part of afterParts) {
            if (isQuantity(part) && !quantity) {
                quantity = part;
            } else if (isDate(part)) {
                if (dateCount === 0) {
                    productionDate = part;
                    dateCount++;
                } else if (dateCount === 1) {
                    expiryDate = part;
                    dateCount++;
                }
            }
        }

        const productName = extractProductName(trimmedLine);
        const isPattern1 = batches.length > 1;

        for (let i = 0; i < batches.length; i++) {
            const batch = batches[i];
            if (batch) {
                products.push({
                    description: (isPattern1 && i !== 0) ? '' : productName,
                    batchNumber: batch,
                    quantity: (isPattern1 && i !== 0) ? '' : quantity,
                    productionDate: (isPattern1 && i !== 0) ? '' : productionDate,
                    expiryDate: (isPattern1 && i !== 0) ? '' : expiryDate
                });
            }
        }
    }

    return products;
};

// --------------------------------------------------------------
// Fetch evaluation data from Checklist Fácil API
// --------------------------------------------------------------
const fetchEvaluationData = async (evaluationId, forceRefresh = false) => {
    if (!forceRefresh && evaluationCache.has(evaluationId)) {
        const cached = evaluationCache.get(evaluationId);
        const cacheAge = Date.now() - cached.timestamp;
        if (cacheAge < CACHE_DURATION) {
            return cached.data;
        }
    }

    checkRateLimit(evaluationId);

    const versions = ['v2', 'v3', 'v4'];
    let lastError = null;

    for (const version of versions) {
        try {
            const url = `${CHECKLIST_API_BASE}/${version}/evaluations/${evaluationId}`;

            const response = await fetchWithRetry(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${CHECKLIST_API_KEY}`,
                    'Accept-Language': 'pt-br',
                    'Content-Type': 'application/json'
                }
            });

            if (response.status === 404) {
                lastError = new Error(`Avaliação não encontrada na ${version}`);
                continue;
            }

            if (!response.ok) {
                const errorText = await response.text();
                lastError = new Error(`Erro ${response.status}: ${errorText}`);
                continue;
            }

            const data = await response.json();
            console.log(`API JSON Response (Version ${version}):`, data);

            const statusCode = data.status || data.situacao;
            if (statusCode !== 6) {
                const statusMap = { 1: 'Não Iniciado', 2: 'Em Andamento', 3: 'Em Análise', 4: 'Reprovado', 5: 'Reaberto', 6: 'Concluído' };
                throw new Error(`A avaliação está com status "${statusMap[statusCode] || 'Desconhecido'}". A API só retorna dados de avaliações CONCLUÍDAS (status 6).`);
            }

            recordRequest(evaluationId);
            evaluationCache.set(evaluationId, { data: data, timestamp: Date.now(), version: version });
            return data;
        } catch (error) {
            console.error(`Erro na ${version}:`, error);
            lastError = error;
        }
    }

    throw lastError || new Error(`Avaliação ${evaluationId} não encontrada. Verifique se o ID está correto e se a avaliação está concluída.`);
};

// --------------------------------------------------------------
// Checkbox helpers
// --------------------------------------------------------------
const CB_CHECKED = '☒';
const CB_UNCHECKED = '☐';

const evaluativeToYesNo = (evaluative) => {
    if (evaluative === 3 || evaluative === 8) return 'yes';
    if (evaluative === 2 || evaluative === 7) return 'no';
    if (evaluative === 1 || evaluative === 6) return 'na';
    return null;
};

// --------------------------------------------------------------
// Format date from API
// --------------------------------------------------------------
const formatApiDate = (dateStr) => {
    if (!dateStr) return '';
    try {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return dateStr;
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    } catch (e) {
        return dateStr;
    }
};

// --------------------------------------------------------------
// Extract just the date part from a date string for grouping
// --------------------------------------------------------------
const extractDatePart = (dateStr) => {
    if (!dateStr) return '';
    const match = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
    if (match) {
        const day = match[1].padStart(2, '0');
        const month = match[2].padStart(2, '0');
        const year = match[3].padStart(4, '0');
        return `${day}/${month}/${year}`;
    }
    if (dateStr.match(/^\d{1,2}:\d{2}/)) {
        const now = new Date();
        const day = now.getDate().toString().padStart(2, '0');
        const month = (now.getMonth() + 1).toString().padStart(2, '0');
        const year = now.getFullYear();
        return `${day}/${month}/${year}`;
    }
    return dateStr;
};

// --------------------------------------------------------------
// Attachment helpers
// --------------------------------------------------------------
const getUrlExtension = (url) => {
    try {
        const pathname = new URL(url).pathname;
        const match = pathname.match(/\.([a-zA-Z0-9]+)$/);
        return match ? match[1].toLowerCase() : 'jpg';
    } catch (e) {
        const clean = String(url).split('?')[0];
        const match = clean.match(/\.([a-zA-Z0-9]+)$/);
        return match ? match[1].toLowerCase() : 'jpg';
    }
};

const sanitizeFileNamePart = (str) => {
    const normalized = String(str || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
    const cleaned = normalized
        .replace(/[^a-zA-Z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .substring(0, 60);
    return cleaned || 'Item';
};

const fetchPhotoAttachments = async (photoAttachments, onProgress) => {
    const results = [];
    const failed = [];

    for (let i = 0; i < photoAttachments.length; i++) {
        const att = photoAttachments[i];
        try {
            const response = await fetch(att.url);
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const blob = await response.blob();

            const categoryFolder = sanitizeFileNamePart(att.categoryName);
            const itemLabel = sanitizeFileNamePart(att.itemName);
            const ext = getUrlExtension(att.url);
            const fileName = `${itemLabel}_${String(att.index).padStart(2, '0')}.${ext}`;

            results.push({
                path: `Fotos/${categoryFolder}/${fileName}`,
                blob
            });
        } catch (err) {
            console.error('Falha ao baixar anexo:', att.url, err);
            failed.push(att);
        }

        if (onProgress) onProgress(i + 1, photoAttachments.length);
    }

    return { results, failed };
};

// --------------------------------------------------------------
// Parse NEW API format
// --------------------------------------------------------------
const parseNewApiFormat = (apiData) => {
    const d = {
        perishableProducts: [],
        documents: [],
        shipmentType: '',
        boardingType: '',
        containerType: '',
        countryOfOriginName: '',
        countryOfOriginOption: '',
        numberOfSamplings: '',
        photoAttachments: [],
        samplingRepresentative: null,
        samplingRepresentativeRemark: '',
        samplingTransportOk: null,
        samplingTransportRemark: '',
        samplingLabAccredited: null,
        samplingLabAccreditedRemark: '',
        inspectorApprovalName: '',
        inspectorApprovalSignatureUrl: '',
        manufacturerApprovalName: '',
        manufacturerApprovalSignatureUrl: '',
        labApprovalName: '',
        labApprovalSignatureUrl: '',
    };

    d.inspectorName = apiData.user?.name || '';
    d.unitName = apiData.unit?.name || '';
    d.dateOfInspection = formatApiDate(apiData.startedAt);
    d.dateOfReport = formatApiDate(apiData.concludedAt) || formatApiDate(apiData.approvedAt) || new Date().toLocaleDateString('pt-BR');

    if (apiData.perimeter?.start?.address) {
        d.placeOfInspection = apiData.perimeter.start.address;
    }

    if (apiData.signatures && apiData.signatures.length > 0) {
        d.signatureUrl = apiData.signatures[0].url || '';
        d.signatureRole = apiData.signatures[0].role || '';
    }

    const getText = (item) => item?.answer?.text || '';
    const getOption = (item) => item?.answer?.selectedOptions?.[0]?.text || '';
    const getOptions = (item) => (item?.answer?.selectedOptions || []).map(o => o.text);
    const getEval = (item) => evaluativeToYesNo(item?.answer?.evaluative);
    const getComment = (item) => item?.comment || '';
    const getNumber = (item) => item?.answer?.number !== null && item?.answer?.number !== undefined ? String(item.answer.number) : '';

    let countryOfOriginOption = '';
    let countryOfOriginName = '';
    let currentSamplingRow = null;
    d.samplingRows = [];
    let currentProductRow = null;
    let productStartNewRow = true;
    d.perishableProducts = [];

    const pendingOtherTargets = {};
    const registerOtherTarget = (item, fieldName) => {
        const unlockId = (item.dependencies || [])
            .flatMap(dep => dep.unlocks || [])
            .find(u => (u?.name || '').trim() === 'Other')?.id;
        if (unlockId) pendingOtherTargets[unlockId] = fieldName;
    };

    for (const cat of (apiData.categories || [])) {
        for (const item of (cat.items || [])) {
            const name = item.name?.trim() || '';

            if (Object.prototype.hasOwnProperty.call(pendingOtherTargets, item.id)) {
                const targetField = pendingOtherTargets[item.id];
                d[targetField] = getText(item);
                delete pendingOtherTargets[item.id];
                continue;
            }

            if (Array.isArray(item.attachments) && item.attachments.length > 0) {
                item.attachments.forEach((att, idx) => {
                    if (!att?.url) return;
                    d.photoAttachments.push({
                        url: att.url,
                        attachmentId: att.id,
                        subtitle: att.subtitle || '',
                        categoryName: cat.name || 'Geral',
                        itemName: name || `Item_${item.id}`,
                        itemId: item.id,
                        index: idx + 1
                    });
                });
            }

            if (name === 'Main country of origin:') {
                countryOfOriginOption = getOption(item);
                continue;
            }
            if (name === 'Main Country of Origin') {
                countryOfOriginName = getOption(item);
                continue;
            }
            if (name.startsWith('Are the samples collected representative')) {
                d.samplingRepresentative = getEval(item);
                d.samplingRepresentativeRemark = getComment(item);
                continue;
            }
            if (name.startsWith('Are the samples transported according to the conditions specified')) {
                d.samplingTransportOk = getEval(item);
                d.samplingTransportRemark = getComment(item);
                continue;
            }
            if (name.startsWith('Are all test parameters performed covered under the laboratory')) {
                d.samplingLabAccredited = getEval(item);
                d.samplingLabAccreditedRemark = getComment(item);
                continue;
            }

            switch (name) {
                case 'Project No (INVOICE)':
                    d.projectNo = getText(item);
                    break;
                case 'Client Name (EXPORT):':
                    d.clientNameExport = getOption(item);
                    break;
                case 'Client Name (IMPORTER):':
                    if (getOption(item) === 'Other') {
                        registerOtherTarget(item, 'clientName');
                    } else {
                        d.clientName = getOption(item) || getText(item);
                    }
                    break;
                case 'Country of Import:':
                    d.countryOfImport = getOption(item) || getText(item);
                    break;
                case 'Client Address:':
                    if (getOption(item) === 'Other') {
                        registerOtherTarget(item, 'clientAddress');
                    } else {
                        d.clientAddress = getOption(item) || getText(item);
                    }
                    break;
                case 'Date of inspection:':
                    d.dateOfInspection = getText(item);
                    break;
                case 'Inspection Valid Till':
                    d.inspectionValidTill = getText(item);
                    break;
                case 'Arrive Insp. Location:':
                    d.arriveInspectionLocation = getText(item);
                    break;
                case 'Date of Report:':
                    d.dateOfReport = getText(item);
                    break;
                case 'Total time:':
                    d.totalTime = getText(item);
                    break;
                case 'Travel time:':
                    d.travelTime = getText(item);
                    break;
                case 'Location (address):':
                    if (!d.placeOfInspection) {
                        d.placeOfInspection = getText(item);
                    }
                    break;
                case 'Full Description:':
                    d.fullDescriptionOption = getOption(item);
                    break;
                case 'Condition of good':
                    d.conditionOfGoods = getOption(item);
                    break;
                case 'Shipment and packing':
                    d.shipmentType = getOption(item);
                    break;
                case 'Type of boarding':
                    d.boardingType = getOption(item);
                    break;
                case 'Type of container':
                    d.containerType = getOption(item);
                    break;
                case 'Container No.':
                    d.containerNo = getText(item);
                    break;
                case 'Agent Seal No.':
                    d.agentSealNo = getNumber(item) || getText(item);
                    break;
                case 'TUV AUSTRIA Seal No.':
                    d.tuvSealNo = getNumber(item) || getText(item);
                    break;
                case 'Storage conditions appropriate:':
                    d.storageConditions = getEval(item);
                    break;
                case 'Are goods presented as per inspection ref. doc?':
                    d.goodsPresentedAsRef = getEval(item);
                    break;
                case 'Was visual identification of goods satifactory?':
                    d.visualIdentification = getEval(item);
                    break;
                case 'Was visual examination of goods  satisfactory?':
                    d.visualExamination = getEval(item);
                    break;
                case 'Packing/Shipping Marks conform to applicable to CAI and/or AIR:':
                    d.packingMarksConform = getEval(item);
                    break;
                case 'Container Details Completed in Attachment':
                    d.containerDetailsAttached = getEval(item);
                    break;
                case 'Gross Weight (kg):':
                    d.grossWeight = getNumber(item) || getText(item);
                    break;
                case 'Net Weight (kg):':
                    d.netWeight = getNumber(item) || getText(item);
                    break;
                case 'Packing Number':
                    d.packingType = getOption(item);
                    d.packingNumber = getComment(item);
                    break;
                case 'Quality Check':
                    d.qualityCheck = getOption(item);
                    break;
                case 'Has non-radioactive certificate been collected?':
                    d.nonRadioactiveCertificate = getEval(item);
                    break;
                case 'Has exporter`s quality system been collected?':
                    d.exporterQualitySystem = getEval(item);
                    break;
                case 'Photographs':
                    d.photographs = getEval(item);
                    break;
                case 'Total Quantity:':
                    d.totalQuantity = getText(item);
                    break;
                case 'Sampling Techniques Used:':
                    d.samplingTechnique = getOption(item);
                    break;
                case 'Number of items spot checked (state quantity or %):':
                    d.itemsSpotChecked = getText(item);
                    break;
                case 'Number of samplings':
                    d.numberOfSamplings = getText(item);
                    break;
                case '1. Is te container clean, free of any foreign impurities, odor, or damage (such as cracks or holes) that may affect the integrity os shipment, and photographed wihle empty?':
                    d.loading_containerClean = getEval(item);
                    break;
                case 'Has the operation of the cooling system (if required) been verified according to the temperatures in the AIR/CAI?':
                    d.loading_coolingVerified = getEval(item);
                    break;
                case 'Has the container been photographed afther the first quarter, half and full load of the shipment?':
                    d.loading_photographed = getEval(item);
                    break;
                case 'Are loading conditions e.g., temperature and humidity (if required) being monitored during the process according to the conditions specified in the AIR/CAI?':
                    d.loading_conditionsMonitored = getEval(item);
                    break;
                case 'Document':
                    d.documents = getOptions(item);
                    break;
                case 'Details (Product Description/ Batch Number/ Quantity/ Production Date/ Expiry Date)':
                    const rawDetails = getText(item);
                    d.productDetails = rawDetails;

                    if (rawDetails) {
                        const parsedProducts = parseProductDetailsSimple(rawDetails);
                        d.perishableProducts = parsedProducts;

                        if (parsedProducts.length > 0) {
                            d.parsedDescription = parsedProducts[0].description;
                            d.parsedBatchNumber = parsedProducts[0].batchNumber;
                            d.parsedQuantity = parsedProducts[0].quantity;
                            d.parsedProductionDate = parsedProducts[0].productionDate;
                            d.parsedExpiryDate = parsedProducts[0].expiryDate;
                            d.parsedProductsCount = parsedProducts.length;
                        }
                    }
                    break;

                case 'Product Description':
                    if (productStartNewRow || !currentProductRow) {
                        currentProductRow = { description: '', batchNumber: '', quantity: '', productionDate: '', expiryDate: '' };
                        d.perishableProducts.push(currentProductRow);
                        productStartNewRow = false;
                    }
                    currentProductRow.description = getOption(item) || getText(item);
                    break;
                case 'Batch Number':
                    if (currentProductRow) currentProductRow.batchNumber = getNumber(item) || getText(item);
                    break;
                case 'Quantity':
                    if (currentProductRow) currentProductRow.quantity = getNumber(item) || getText(item);
                    break;
                case 'Production Date':
                    if (currentProductRow) currentProductRow.productionDate = getText(item);
                    break;
                case 'Expiry Date':
                    if (currentProductRow) currentProductRow.expiryDate = getText(item);
                    break;
                case 'New registration?':
                    productStartNewRow = (getEval(item) === 'yes');
                    break;
                case 'Was sealling and locking of the containers carried out immediately after the loading inspection was completed?':
                    d.sealing_sealingLocking = getEval(item);
                    break;
                case 'Has the AIR checklist been completed and attached to the inspection report?':
                    d.sealing_airChecklist = getEval(item);
                    break;
                case 'Have all photographs related to the sealing process been attached to the inspection report?':
                    d.sealing_sealingPhotos = getEval(item);
                    break;
                case 'Have all photographs related to the inspection been attached to the inspection report?':
                    d.sealing_inspectionPhotos = getEval(item);
                    break;
                case 'Inspection Results':
                    d.inspectionResult = getOption(item);
                    break;
                case 'Manufacturer Name / Address':
                    d.manufacturerNameAddress = getText(item);
                    break;
                case 'Time':
                    d.samplingTime = getText(item);
                    break;
                case 'Data':
                    break;
                case 'Product Name':
                    currentSamplingRow = {
                        data: '',
                        productName: getOption(item) || getText(item),
                        sampleQuantity: '',
                        caiNumber: '',
                        testStandard: '',
                        siteSample: '',
                        description: '',
                        sampleCodes: '',
                        labName: ''
                    };
                    d.samplingRows.push(currentSamplingRow);
                    break;
                case 'Sample  Quantity':
                    if (currentSamplingRow) currentSamplingRow.sampleQuantity = getNumber(item) || getText(item);
                    break;
                case 'Applicable CAI  Number (link)':
                    if (currentSamplingRow) currentSamplingRow.caiNumber = getOption(item) || getText(item);
                    break;
                case 'Test Standard':
                    if (currentSamplingRow) currentSamplingRow.testStandard = getOption(item) || getText(item);
                    break;
                case 'Site Sample':
                    if (currentSamplingRow) currentSamplingRow.siteSample = getOption(item) || getText(item);
                    break;
                case 'Sampling  description':
                    if (currentSamplingRow) currentSamplingRow.description = getText(item);
                    break;
                case 'Sample Codes /  Sticker / Seal':
                    if (currentSamplingRow) currentSamplingRow.sampleCodes = getText(item);
                    break;
                case 'Lab Name':
                    if (currentSamplingRow) currentSamplingRow.labName = getText(item);
                    break;
                case 'Name Surname (Inspector Approval)':
                    d.inspectorApprovalName = getText(item);
                    d.inspectorApprovalSignatureUrl = item.attachments?.[0]?.url || '';
                    break;
                case 'Name Surname (Manufacturer/Exporter Approval)':
                    d.manufacturerApprovalName = getText(item);
                    d.manufacturerApprovalSignatureUrl = item.attachments?.[0]?.url || '';
                    break;
                case 'Name Surname (Laboratory Approval)':
                    d.labApprovalName = getText(item);
                    d.labApprovalSignatureUrl = item.attachments?.[0]?.url || '';
                    break;
                default:
                    break;
            }
        }
    }

    let samplingRowIndex = 0;
    for (const cat of (apiData.categories || [])) {
        for (const item of (cat.items || [])) {
            const name = item.name?.trim() || '';
            if (name === 'Data') {
                const dataValue = getText(item);
                if (samplingRowIndex < d.samplingRows.length) {
                    d.samplingRows[samplingRowIndex].data = dataValue;
                    samplingRowIndex++;
                }
            }
        }
    }

    d.countryOfOriginOption = countryOfOriginOption;
    d.countryOfOriginName = countryOfOriginName;

    if (!d.projectNo) d.projectNo = '';
    if (!d.countryOfImport) d.countryOfImport = '';

    return d;
};

// --------------------------------------------------------------
// Group sampling rows by date
// --------------------------------------------------------------
const groupSamplingRowsByDate = (samplingRows) => {
    const groups = new Map();

    for (const row of samplingRows) {
        const dateKey = extractDatePart(row.data || '');
        if (!groups.has(dateKey)) {
            groups.set(dateKey, []);
        }
        groups.get(dateKey).push(row);
    }

    const result = [];
    for (const [date, rows] of groups) {
        result.push({ date, rows });
    }

    result.sort((a, b) => {
        const dateA = parseDateString(a.date);
        const dateB = parseDateString(b.date);
        if (dateA && dateB) {
            return dateB - dateA;
        }
        return a.date.localeCompare(b.date);
    });

    return result;
};

// --------------------------------------------------------------
// Parse date string for sorting
// --------------------------------------------------------------
const parseDateString = (dateStr) => {
    if (!dateStr) return null;
    try {
        const parts = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
        if (parts) {
            const day = parseInt(parts[1], 10);
            const month = parseInt(parts[2], 10) - 1;
            const year = parseInt(parts[3], 10);
            return new Date(year, month, day).getTime();
        }
        const timeParts = dateStr.match(/^(\d{1,2}):(\d{2})/);
        if (timeParts) {
            const now = new Date();
            const hours = parseInt(timeParts[1], 10);
            const minutes = parseInt(timeParts[2], 10);
            return new Date(now.getFullYear(), now.getMonth(), now.getDate(), hours, minutes).getTime();
        }
        return null;
    } catch (e) {
        return null;
    }
};

// --------------------------------------------------------------
// Column-letter helpers
// --------------------------------------------------------------
const colLetterToIndex = (letters) => {
    let idx = 0;
    for (let i = 0; i < letters.length; i += 1) {
        idx = idx * 26 + (letters.charCodeAt(i) - 64);
    }
    return idx;
};

const parseCellRange = (range) => {
    const [from, to] = range.split(':');
    const fromMatch = from.match(/([A-Z]+)(\d+)/);
    const toMatch = to.match(/([A-Z]+)(\d+)/);
    return {
        fromCol: colLetterToIndex(fromMatch[1]),
        toCol: colLetterToIndex(toMatch[1]),
        fromRow: parseInt(fromMatch[2], 10),
        toRow: parseInt(toMatch[2], 10),
    };
};

const colWidthToPx = (chars) => Math.round((chars || 8.43) * 7 + 5);
const ptToPx = (pt) => Math.round((pt || 15) * 96 / 72);

const rangeSizePx = (ws, range) => {
    const { fromCol, toCol, fromRow, toRow } = parseCellRange(range);
    let width = 0;
    for (let c = fromCol; c <= toCol; c += 1) {
        width += colWidthToPx(ws.getColumn(c).width);
    }
    let height = 0;
    for (let r = fromRow; r <= toRow; r += 1) {
        height += ptToPx(ws.getRow(r).height);
    }
    return { fromCol, fromRow, width, height };
};

const SAMPLING_ROW_LINE_HEIGHT_PT = 13.5;
const MAX_SAMPLING_ROW_LINES = 8;

const growSamplingRowForText = (ws, rowNum, fieldsWithCols) => {
    let maxLines = 1;
    fieldsWithCols.forEach(({ text, cols }) => {
        const anchorCoord = `${cols[0]}${rowNum}`;
        const cell = ws.getCell(anchorCoord);
        cell.alignment = { ...cell.alignment, vertical: 'top' };

        if (!text) return;
        const widthChars = cols.reduce((sum, col) => sum + (ws.getColumn(col).width || 8.43), 0);
        const segments = String(text).split('\n');
        const lines = segments.reduce(
            (total, seg) => total + Math.max(1, Math.ceil(seg.length / Math.max(1, widthChars))),
            0
        );
        if (lines > maxLines) maxLines = lines;
    });

    const cappedLines = Math.min(maxLines, MAX_SAMPLING_ROW_LINES);
    const neededHeight = cappedLines * SAMPLING_ROW_LINE_HEIGHT_PT;
    const row = ws.getRow(rowNum);
    if (!row.height || row.height < neededHeight) {
        row.height = neededHeight;
    }
};

const SAMPLING_ROW_FIELD_COLS = [
    { key: 'productName', cols: ['B', 'C'] },
    { key: 'caiNumber', cols: ['G', 'H', 'I'] },
    { key: 'testStandard', cols: ['J', 'K'] },
    { key: 'siteSample', cols: ['L', 'M', 'N'] },
    { key: 'description', cols: ['O', 'P', 'Q', 'R'] },
    { key: 'sampleCodes', cols: ['S', 'T', 'U'] },
    { key: 'labName', cols: ['V'] },
];

const applySamplingRowHeight = (ws, rowNum, row) => {
    growSamplingRowForText(
        ws,
        rowNum,
        SAMPLING_ROW_FIELD_COLS.map(({ key, cols }) => ({ text: row[key], cols }))
    );
};

const embedSignatureImage = async (wb, ws, url, range) => {
    if (!url) return;
    try {
        const response = await fetch(url);
        if (!response.ok) return;
        const arrayBuffer = await response.arrayBuffer();
        const ext = (getUrlExtension(url) || 'jpg').toLowerCase();
        const extension = ext === 'png' ? 'png' : (ext === 'gif' ? 'gif' : 'jpeg');
        const imageId = wb.addImage({ buffer: arrayBuffer, extension });

        const { fromCol, fromRow, width, height } = rangeSizePx(ws, range);
        const margin = 4;
        ws.addImage(imageId, {
            tl: { col: fromCol - 1, row: fromRow - 1 },
            ext: { width: Math.max(20, width - margin), height: Math.max(20, height - margin) },
            editAs: 'oneCell',
        });
    } catch (err) {
        console.error('Falha ao inserir imagem de assinatura na planilha:', url, err);
    }
};

const applySamplingApprovals = async (wb, ws, d, checked, unchecked) => {
    ws.getCell('P42').value = d.samplingRepresentative === 'yes' ? checked : unchecked;
    ws.getCell('R42').value = d.samplingRepresentative === 'no' ? checked : unchecked;
    ws.getCell('S42').value = d.samplingRepresentative === 'na' ? checked : unchecked;
    ws.getCell('U42').value = d.samplingRepresentativeRemark || '';

    ws.getCell('P47').value = d.samplingTransportOk === 'yes' ? checked : unchecked;
    ws.getCell('R47').value = d.samplingTransportOk === 'no' ? checked : unchecked;
    ws.getCell('S47').value = d.samplingTransportRemark || '';

    ws.getCell('P49').value = d.samplingLabAccredited === 'yes' ? checked : unchecked;
    ws.getCell('R49').value = d.samplingLabAccredited === 'no' ? checked : unchecked;
    ws.getCell('S49').value = d.samplingLabAccreditedRemark || '';

    ws.getCell('A60').value = d.inspectorApprovalName || d.inspectorName || '';
    ws.getCell('I60').value = d.manufacturerApprovalName || '';
    ws.getCell('Q60').value = d.labApprovalName || '';

    await embedSignatureImage(wb, ws, d.inspectorApprovalSignatureUrl, 'A62:H62');
    await embedSignatureImage(wb, ws, d.manufacturerApprovalSignatureUrl, 'I62:P62');
    await embedSignatureImage(wb, ws, d.labApprovalSignatureUrl, 'Q62:V62');
};

const loadSamplingTemplateBuffer = async () => {
    const templateResponse = await fetch('/templates/Sampling_Data_sheet.xlsx');
    if (!templateResponse.ok) {
        throw new Error('Falha ao carregar o template Sampling_Data_sheet.xlsx');
    }
    return await templateResponse.arrayBuffer();
};

const sumSampleQuantities = (rows) => {
    let total = 0;
    let sawNumber = false;

    for (const row of (rows || [])) {
        const raw = row?.sampleQuantity;
        if (raw === null || raw === undefined || raw === '') continue;

        const normalised = String(raw).replace(',', '.').replace(/[^0-9.-]/g, '');
        const value = parseFloat(normalised);
        if (!Number.isFinite(value)) continue;

        total += value;
        sawNumber = true;
    }

    if (!sawNumber) return null;
    return Math.round(total * 1000) / 1000;
};

const generateSingleSamplingDataSheet = async (d, dateGroup, preloadedTemplate = null) => {
    const checked = CB_CHECKED;
    const unchecked = CB_UNCHECKED;

    const templateArrayBuffer = preloadedTemplate || await loadSamplingTemplateBuffer();

    const wb = new ExcelJS.Workbook();
    await wb.xlsx.load(templateArrayBuffer);
    const ws = wb.getWorksheet('Table 1') || wb.worksheets[0];

    const set = (coord, value) => {
        ws.getCell(coord).value = value;
    };

    set('H6', d.projectNo || '');
    set('T6', d.dateOfInspection || '');
    set('H7', d.clientName || '');
    set('T7', d.manufacturerNameAddress || '');
    set('H8', d.inspectorName || '');
    set('T8', [d.dateOfInspection, d.samplingTime, d.placeOfInspection].filter(Boolean).join(' / '));

    const rows = dateGroup.rows.slice(0, 10);

    const totalSamples = sumSampleQuantities(rows);
    set('F15', totalSamples !== null ? totalSamples : (d.numberOfSamplings || ''));

    rows.forEach((row, idx) => {
        const r = 30 + idx;
        set(`B${r}`, row.productName || '');
        set(`D${r}`, row.sampleQuantity || '');
        set(`G${r}`, row.caiNumber || '');
        set(`J${r}`, row.testStandard || '');
        set(`L${r}`, row.siteSample || '');
        set(`O${r}`, row.description || '');
        set(`S${r}`, row.sampleCodes || '');
        set(`V${r}`, row.labName || '');
        applySamplingRowHeight(ws, r, row);
    });

    await applySamplingApprovals(wb, ws, d, checked, unchecked);

    return wb;
};

const generateSamplingDataSheetWorkbooks = async (d) => {
    const templateArrayBuffer = await loadSamplingTemplateBuffer();
    const groups = groupSamplingRowsByDate(d.samplingRows || []);

    if (groups.length === 0) {
        const wb = new ExcelJS.Workbook();
        await wb.xlsx.load(templateArrayBuffer);
        return [{ date: '', rowCount: 0, wb }];
    }

    const workbooks = [];
    for (const group of groups) {
        const wb = await generateSingleSamplingDataSheet(d, group, templateArrayBuffer);
        workbooks.push({ date: group.date, rowCount: group.rows.length, wb });
    }
    return workbooks;
};

const dateLabelForFileName = (date) => {
    const cleaned = String(date || '').trim();
    if (!cleaned) return 'sem_data';
    return cleaned.replace(/[^0-9A-Za-z]+/g, '-').replace(/^-+|-+$/g, '') || 'sem_data';
};

const generateInspectionXlsx = async (d) => {
    const checked = CB_CHECKED;
    const unchecked = CB_UNCHECKED;
    const yn = (val) => val === 'yes' ? checked : unchecked;
    const no = (val) => val === 'no' ? checked : unchecked;
    const na = (val) => val === 'na' ? checked : unchecked;

    const fillCheckboxInText = (text, replacements) => {
        let count = -1;
        return text.replace(/☐/g, (match) => {
            count++;
            const r = replacements.find(x => x.index === count);
            if (r) return r.checked ? checked : unchecked;
            return match;
        });
    };

    const templateResponse = await fetch('/templates/Empty_Inspection_report.xlsx');
    const templateArrayBuffer = await templateResponse.arrayBuffer();

    const wb = new ExcelJS.Workbook();
    await wb.xlsx.load(templateArrayBuffer);
    const ws = wb.getWorksheet('Sheet1');

    const ROW_OFFSET = 5;

    const set = (coord, value) => {
        const match = coord.match(/^([A-Z]+)(\d+)$/);
        const finalCoord = match
            ? `${match[1]}${parseInt(match[2], 10) + ROW_OFFSET}`
            : coord;

        const cell = ws.getCell(finalCoord);
        cell.value = value;
    };

    set('G1', d.projectNo || '');
    set('T1', d.countryOfImport || '');
    set('G2', d.clientName || '');
    set('T2', d.clientAddress || '');
    set('B5', `Date of Inspection: ${d.dateOfInspection || ''}`);
    set('H5', `Date of Report: ${d.dateOfReport || ''}`);
    set('B6', `Inspection Valid Till: ${d.inspectionValidTill || ''}`);
    set('B7', `Arrive Insp. Location: ${d.arriveInspectionLocation || ''}`);
    set('H7', `Total Time: ${d.totalTime || ''}`);
    set('B8', `Leave Insp. Location: ${d.arriveInspectionLocation || ''}`);
    set('H8', `Travel Time: ${d.travelTime || ''}`);
    set('O5', `Location (Address):\n\n${d.placeOfInspection || ''}`);

    set('P11', `Invoice No:  ${d.projectNo || ''}`);
    set('U11', `Invoice Date: ${d.dateOfInspection || ''}`);

    const cellU11 = ws.getCell(`U${11 + ROW_OFFSET}`);
    cellU11.font = {
        name: 'Calibri',
        size: 9,
        bold: false
    };

    const fullDescOption = d.fullDescriptionOption || '';
    const isAvailableOnDocs = fullDescOption.toLowerCase().includes('available') || fullDescOption.toLowerCase().includes('commercial');
    const isCompletedInAttach = fullDescOption.toLowerCase().includes('attachment');

    set('B12', fillCheckboxInText(
        'Full Description:\n☐ Available/Completed on commercial documents    ☐  Completed in attachment',
        [
            { index: 0, checked: isAvailableOnDocs },
            { index: 1, checked: isCompletedInAttach },
        ]
    ));

    const condRaw = (d.conditionOfGoods || '').toLowerCase();
    set('O12', fillCheckboxInText(
        'Condition of goods: \n   ☐  New     ☐  Used   ☐ Reconditioned  ',
        [
            { index: 0, checked: condRaw === 'new' },
            { index: 1, checked: condRaw === 'used' },
            { index: 2, checked: condRaw === 'reconditioned' },
        ]
    ));

    const ship = (d.shipmentType || '').toLowerCase().trim();
    const boarding = (d.boardingType || '').toLowerCase().trim();
    const container = (d.containerType || '').toLowerCase().trim();

    const is20 = container === '20\'' || container === '20' || container === '20ft' || container.includes('20');
    const is40 = container === '40\'' || container === '40' || container === '40ft' || container.includes('40');
    const isBulk = container === 'bulk' || container.includes('bulk');

    const isFCL = boarding === 'fcl' || boarding.includes('fcl') || boarding.includes('full container load');
    const isLCL = boarding === 'lcl' || boarding.includes('lcl') || boarding.includes('less than container load');
    const isConventional = boarding === 'conventional' || boarding.includes('conventional');

    const isSea = ship === 'sea' || ship.includes('sea');
    const isRoad = ship === 'road' || ship.includes('road');
    const isAir = ship === 'air' || ship.includes('air');

    const row15Options = {
        'Sea': isSea,
        'Road': isRoad,
        'Air': isAir,
        'Conventional': isConventional,
        'LCL': isLCL,
        'FCL': isFCL,
        '20’': is20,
        '20\'': is20,
        '40’': is40,
        '40\'': is40,
        'Bulk': isBulk
    };

    const row15 = ws.getRow(15 + ROW_OFFSET);
    row15.eachCell((cell) => {
        let val = cell.value;
        if (!val) return;

        let text = '';
        if (typeof val === 'object' && val.richText) {
            text = val.richText.map(rt => rt.text).join('');
        } else {
            text = val.toString();
        }

        let newText = text;

        for (const [label, isChecked] of Object.entries(row15Options)) {
            const escapedLabel = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const regexBefore = new RegExp(`([☐☒])(\\s*${escapedLabel})`, 'gi');
            newText = newText.replace(regexBefore, (m, p1, p2) => (isChecked ? '☒' : '☐') + p2);
            const regexAfter = new RegExp(`(${escapedLabel}\\s*)([☐☒])`, 'gi');
            newText = newText.replace(regexAfter, (m, p1, p2) => p1 + (isChecked ? '☒' : '☐'));
        }

        if (newText !== text) {
            cell.value = newText;
        }
    });

    set('B17', d.containerNo || '');
    set('E17', d.agentSealNo || '');
    set('I17', d.tuvSealNo || '');

    set('L17', fillCheckboxInText(
        'Storage conditions appropriate:  ☐ Yes    ☐ No  ',
        [
            { index: 0, checked: d.storageConditions === 'yes' },
            { index: 1, checked: d.storageConditions === 'no' },
        ]
    ));
    set('L18', fillCheckboxInText(
        'Are goods presented as per inspection ref. doc?     ☐ Yes    ☐ No  ',
        [
            { index: 0, checked: d.goodsPresentedAsRef === 'yes' },
            { index: 1, checked: d.goodsPresentedAsRef === 'no' },
        ]
    ));
    set('L19', fillCheckboxInText(
        'Was visual identification of goods satisfactory?  ☐ Yes    ☐ No  ',
        [
            { index: 0, checked: d.visualIdentification === 'yes' },
            { index: 1, checked: d.visualIdentification === 'no' },
        ]
    ));
    set('L20', fillCheckboxInText(
        'Was visual examination of goods satisfactory?          ☐ Yes    ☐ No  ',
        [
            { index: 0, checked: d.visualExamination === 'yes' },
            { index: 1, checked: d.visualExamination === 'no' },
        ]
    ));
    set('L21', fillCheckboxInText(
        'Packing/Shipping Marks conform to applicable to CAI and/or AIR:   ☐ Yes    ☐ No  ',
        [
            { index: 0, checked: d.packingMarksConform === 'yes' },
            { index: 1, checked: d.packingMarksConform === 'no' },
        ]
    ));
    set('L22', fillCheckboxInText(
        'Container Details Completed in Attachment             ☐ Yes    ☐ No  ',
        [
            { index: 0, checked: d.containerDetailsAttached === 'yes' },
            { index: 1, checked: d.containerDetailsAttached === 'no' },
        ]
    ));

    const packType = (d.packingType || '').toLowerCase();
    const qCheck = (d.qualityCheck || '').toLowerCase();

    set('B25',
        `Quantity Check: \nGross Weight: ${d.grossWeight || ''}        Net Weight: ${d.netWeight || ''}\n\n` +
        `Packing Number: ${d.packingNumber || ''}\n` +
        fillCheckboxInText(
            '☐Boxes   ☐Rolls  ☐Pallet   ☐Bundles \n\n☐Bags    ☐Cartons     ☐Others: \n\nQuality Check: \n☐ Certificate of Analysis provided  ☐ Subjected to Analysis/Test\n' +
            'Has Non-radioactive certificate been collected?        ☐ Yes    ☐ No\n' +
            'Has exporter\'s quality system been collected?   ☐ Yes     ☐ No',
            [
                { index: 0, checked: packType === 'boxes' },
                { index: 1, checked: packType === 'rolls' },
                { index: 2, checked: packType === 'pallet' },
                { index: 3, checked: packType === 'bundles' },
                { index: 4, checked: packType === 'bags' },
                { index: 5, checked: packType === 'cartons' },
                { index: 6, checked: packType === 'others' },
                { index: 7, checked: qCheck.includes('certificate of analysis') },
                { index: 8, checked: qCheck.includes('subjected') || qCheck.includes('analysis/test') },
                { index: 9, checked: d.nonRadioactiveCertificate === 'yes' },
                { index: 10, checked: d.nonRadioactiveCertificate === 'no' },
                { index: 11, checked: d.exporterQualitySystem === 'yes' },
                { index: 12, checked: d.exporterQualitySystem === 'no' },
            ]
        )
    );

    const sampTech = (d.samplingTechnique || '').toLowerCase();
    set('M25',
        fillCheckboxInText(
            'Photographs:  ☐ Yes     ☐ No\n',
            [
                { index: 0, checked: d.photographs === 'yes' },
                { index: 1, checked: d.photographs === 'no' },
            ]
        ) +
        `Total Quantity: ${d.totalQuantity || ''}       / Checked Quantity on AQL (Link): \n\n` +
        fillCheckboxInText(
            'Sampling Techniques Used:\n100%   ☐  Spot check (Randomly)  ☐    Sampling plan (as per AQL)  ☐\n\n',
            [
                { index: 0, checked: sampTech.includes('100%') },
                { index: 1, checked: sampTech.includes('spot') },
                { index: 2, checked: sampTech.includes('aql') || sampTech.includes('sampling plan') },
            ]
        ) +
        `Number of items spot checked (state quantity or %) :\n${d.itemsSpotChecked || ''}`
    );

    set('R29', yn(d.loading_containerClean));
    set('S29', no(d.loading_containerClean));
    set('R30', yn(d.loading_coolingVerified));
    set('S30', no(d.loading_coolingVerified));
    set('T30', na(d.loading_coolingVerified));
    set('R32', yn(d.loading_photographed));
    set('S32', no(d.loading_photographed));
    set('T32', na(d.loading_photographed));
    set('R33', yn(d.loading_conditionsMonitored));
    set('S33', no(d.loading_conditionsMonitored));
    set('T33', na(d.loading_conditionsMonitored));

    const originOpt = (d.countryOfOriginOption || '').toLowerCase();
    const originName = d.countryOfOriginName || '';

    let originText = `Main country of origin: ${originName}\n`;
    originText += `☐ Available/Completed on commercial documents\n`;
    originText += `☐ Completed in attachment`;

    set('B36', fillCheckboxInText(
        originText,
        [
            { index: 0, checked: originOpt.includes('available') || originOpt.includes('commercial') },
            { index: 1, checked: originOpt.includes('attachment') },
        ]
    ));

    const docs = d.documents || [];
    const hasDoc = (keyword) => docs.some(doc => doc.toLowerCase().includes(keyword.toLowerCase()));
    set('M36', fillCheckboxInText(
        '☐ Invoice    ☐ Packing List   ☐ Price List   ☐ Technical Sheet\n☐ Catalogues  ☐ Certificate of Analysis ☐ Analysis results\n☐ Special product report  ☐ Description of Goods Attachment\n☐ Equipment\'s calibration certificate (if any)',
        [
            { index: 0, checked: hasDoc('Invoice') },
            { index: 1, checked: hasDoc('Packing List') },
            { index: 2, checked: hasDoc('Price List') },
            { index: 3, checked: hasDoc('Technical Sheet') },
            { index: 4, checked: hasDoc('Catalogues') },
            { index: 5, checked: hasDoc('Certificate of Analysis') },
            { index: 6, checked: hasDoc('Analysis results') },
            { index: 7, checked: hasDoc('Special product report') },
            { index: 8, checked: hasDoc('Description of Goods') },
            { index: 9, checked: hasDoc('calibration') },
        ]
    ));

    const maxRows = 13;
    const flatProducts = d.perishableProducts || [];

    for (let i = 0; i < maxRows; i++) {
        const rowNum = 39 + i;
        if (i < flatProducts.length) {
            const prod = flatProducts[i];
            set(`B${rowNum}`, prod.description || '');
            set(`F${rowNum}`, prod.batchNumber || '');
            set(`J${rowNum}`, prod.quantity || '');
            set(`Q${rowNum}`, prod.productionDate || '');
            set(`U${rowNum}`, prod.expiryDate || '');
        } else {
            set(`B${rowNum}`, '');
            set(`F${rowNum}`, '');
            set(`J${rowNum}`, '');
            set(`Q${rowNum}`, '');
            set(`U${rowNum}`, '');
        }
    }

    set('R53', yn(d.sealing_sealingLocking));
    set('S53', no(d.sealing_sealingLocking));
    set('R54', yn(d.sealing_airChecklist));
    set('S54', no(d.sealing_airChecklist));
    set('R55', yn(d.sealing_sealingPhotos));
    set('S55', no(d.sealing_sealingPhotos));
    set('R56', yn(d.sealing_inspectionPhotos));
    set('S56', no(d.sealing_inspectionPhotos));

    const result = (d.inspectionResult || '').toLowerCase();
    set('B59', fillCheckboxInText(
        '☐ Satisfactory    ☐ Unsatisfactory    ☐ Deferred\n\nAdditional Information / Reason for Result Other Than Satisfactory:  \n',
        [
            { index: 0, checked: result === 'satisfactory' },
            { index: 1, checked: result === 'unsatisfactory' },
            { index: 2, checked: result === 'deferred' },
        ]
    ));

    set('B61', d.inspectorName || '');
    set('M61', d.inspectorName || '');

    const buffer = await wb.xlsx.writeBuffer();
    return buffer;
};

const generateMonthlyReportXlsx = async (monthReports, year, month) => {
    const wb = new ExcelJS.Workbook();
    wb.creator = 'TÜV AUSTRIA Reports';
    wb.created = new Date();

    const monthName = new Date(year, month - 1, 1)
        .toLocaleString('en-US', { month: 'long' });

    const summary = wb.addWorksheet('Summary');
    summary.columns = [
        { header: 'Item', key: 'label', width: 40 },
        { header: 'Value', key: 'value', width: 24 },
    ];
    const sumHead = summary.getRow(1);
    sumHead.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    sumHead.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0D47A1' } };

    const total = monthReports.length;
    const downloaded = monthReports.filter(r => r.downloaded).length;
    const notDownloaded = total - downloaded;
    const satisf = monthReports.filter(r =>
        (r.inspectionResult || '').toLowerCase() === 'satisfactory').length;
    const unsat = monthReports.filter(r =>
        (r.inspectionResult || '').toLowerCase() === 'unsatisfactory').length;
    const deferred = monthReports.filter(r =>
        (r.inspectionResult || '').toLowerCase() === 'deferred').length;
    const totalFiles = monthReports.reduce((s, r) => s + r.filesCount, 0);
    const totalPhotos = monthReports.reduce((s, r) => s + r.photosCount, 0);

    summary.addRow({ label: 'Period', value: `${monthName} ${year}` });
    summary.addRow({ label: 'Total Reports', value: total });
    summary.addRow({ label: 'Downloaded', value: downloaded });
    summary.addRow({ label: 'Not Downloaded', value: notDownloaded });
    summary.addRow({ label: 'Satisfactory', value: satisf });
    summary.addRow({ label: 'Unsatisfactory', value: unsat });
    summary.addRow({ label: 'Deferred', value: deferred });
    summary.addRow({ label: 'Total Files', value: totalFiles });
    summary.addRow({ label: 'Total Photos', value: totalPhotos });

    const details = wb.addWorksheet('Reports');
    details.columns = [
        { header: 'Project No', key: 'projectNo', width: 18 },
        { header: 'Client', key: 'clientName', width: 28 },
        { header: 'Country', key: 'countryOfImport', width: 16 },
        { header: 'Container', key: 'containerNo', width: 16 },
        { header: 'Inspector', key: 'inspectorName', width: 22 },
        { header: 'Result', key: 'inspectionResult', width: 16 },
        { header: 'Date of Inspection', key: 'dateOfInspection', width: 18 },
        { header: 'Report Date', key: 'dateOfReport', width: 18 },
        { header: 'Created By', key: 'createdBy', width: 22 },
        { header: 'Created At', key: 'createdAt', width: 20 },
        { header: 'Files', key: 'filesCount', width: 8 },
        { header: 'Photos', key: 'photosCount', width: 8 },
        { header: 'Downloaded?', key: 'downloaded', width: 14 },
        { header: 'Download Count', key: 'downloadCount', width: 15 },
        { header: 'Last Download', key: 'lastDownloadAt', width: 20 },
        { header: 'Last Download By', key: 'lastDownloadBy', width: 24 },
        { header: 'Last Format', key: 'lastFormat', width: 12 },
    ];

    const hdr = details.getRow(1);
    hdr.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    hdr.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0D47A1' } };
    hdr.alignment = { vertical: 'middle', horizontal: 'center' };
    details.views = [{ state: 'frozen', ySplit: 1 }];

    const fmtDateTime = (iso) => {
        if (!iso) return '';
        const d = new Date(iso);
        if (isNaN(d.getTime())) return String(iso);
        return d.toLocaleDateString('pt-BR') + ' ' +
            d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    };

    monthReports.forEach(r => {
        const row = details.addRow({
            projectNo: r.projectNo || '',
            clientName: r.clientName || '',
            countryOfImport: r.countryOfImport || '',
            containerNo: r.containerNo || '',
            inspectorName: r.inspectorName || '',
            inspectionResult: r.inspectionResult || '',
            dateOfInspection: r.dateOfInspection || '',
            dateOfReport: r.dateOfReport || '',
            createdBy: r.createdBy || '',
            createdAt: fmtDateTime(r.createdAt),
            filesCount: r.filesCount,
            photosCount: r.photosCount,
            downloaded: r.downloaded ? 'Yes' : 'No',
            downloadCount: r.downloadCount,
            lastDownloadAt: fmtDateTime(r.lastDownloadAt),
            lastDownloadBy: r.lastDownloadBy || '',
            lastFormat: r.lastDownloadFormat ? r.lastDownloadFormat.toUpperCase() : '',
        });

        const color = r.downloaded ? 'FFECFDF5' : 'FFFEF3F2';
        row.eachCell(cell => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: color } };
        });
    });

    details.autoFilter = {
        from: 'A1',
        to: { row: 1, column: details.columnCount },
    };

    return await wb.xlsx.writeBuffer();
};

const generateDateRangeReportXlsx = async (rangeReports, startDate, endDate) => {
    const wb = new ExcelJS.Workbook();
    wb.creator = 'TÜV AUSTRIA Reports';
    wb.created = new Date();

    const fmtInputDate = (d) => {
        if (!d) return '';
        const date = new Date(d);
        if (isNaN(date.getTime())) return String(d);
        return date.toLocaleDateString('pt-BR');
    };

    const periodLabel = `${fmtInputDate(startDate)} - ${fmtInputDate(endDate)}`;

    const summary = wb.addWorksheet('Summary');
    summary.columns = [
        { header: 'Item', key: 'label', width: 40 },
        { header: 'Value', key: 'value', width: 24 },
    ];
    const sumHead = summary.getRow(1);
    sumHead.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    sumHead.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0D47A1' } };

    const total = rangeReports.length;
    const downloaded = rangeReports.filter(r => r.downloaded).length;
    const notDownloaded = total - downloaded;
    const satisf = rangeReports.filter(r =>
        (r.inspectionResult || '').toLowerCase() === 'satisfactory').length;
    const unsat = rangeReports.filter(r =>
        (r.inspectionResult || '').toLowerCase() === 'unsatisfactory').length;
    const deferred = rangeReports.filter(r =>
        (r.inspectionResult || '').toLowerCase() === 'deferred').length;
    const totalFiles = rangeReports.reduce((s, r) => s + r.filesCount, 0);
    const totalPhotos = rangeReports.reduce((s, r) => s + r.photosCount, 0);

    summary.addRow({ label: 'Period', value: periodLabel });
    summary.addRow({ label: 'Total Reports', value: total });
    summary.addRow({ label: 'Downloaded', value: downloaded });
    summary.addRow({ label: 'Not Downloaded', value: notDownloaded });
    summary.addRow({ label: 'Satisfactory', value: satisf });
    summary.addRow({ label: 'Unsatisfactory', value: unsat });
    summary.addRow({ label: 'Deferred', value: deferred });
    summary.addRow({ label: 'Total Files', value: totalFiles });
    summary.addRow({ label: 'Total Photos', value: totalPhotos });

    const details = wb.addWorksheet('Reports');
    details.columns = [
        { header: 'Project No', key: 'projectNo', width: 18 },
        { header: 'Client', key: 'clientName', width: 28 },
        { header: 'Country', key: 'countryOfImport', width: 16 },
        { header: 'Container', key: 'containerNo', width: 16 },
        { header: 'Inspector', key: 'inspectorName', width: 22 },
        { header: 'Result', key: 'inspectionResult', width: 16 },
        { header: 'Date of Inspection', key: 'dateOfInspection', width: 18 },
        { header: 'Report Date', key: 'dateOfReport', width: 18 },
        { header: 'Created By', key: 'createdBy', width: 22 },
        { header: 'Created At', key: 'createdAt', width: 20 },
        { header: 'Files', key: 'filesCount', width: 8 },
        { header: 'Photos', key: 'photosCount', width: 8 },
        { header: 'Downloaded?', key: 'downloaded', width: 14 },
        { header: 'Download Count', key: 'downloadCount', width: 15 },
        { header: 'Last Download', key: 'lastDownloadAt', width: 20 },
        { header: 'Last Download By', key: 'lastDownloadBy', width: 24 },
        { header: 'Last Format', key: 'lastFormat', width: 12 },
    ];

    const hdr = details.getRow(1);
    hdr.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    hdr.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0D47A1' } };
    hdr.alignment = { vertical: 'middle', horizontal: 'center' };
    details.views = [{ state: 'frozen', ySplit: 1 }];

    const fmtDateTime = (iso) => {
        if (!iso) return '';
        const d = new Date(iso);
        if (isNaN(d.getTime())) return String(iso);
        return d.toLocaleDateString('pt-BR') + ' ' +
            d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    };

    rangeReports.forEach(r => {
        const row = details.addRow({
            projectNo: r.projectNo || '',
            clientName: r.clientName || '',
            countryOfImport: r.countryOfImport || '',
            containerNo: r.containerNo || '',
            inspectorName: r.inspectorName || '',
            inspectionResult: r.inspectionResult || '',
            dateOfInspection: r.dateOfInspection || '',
            dateOfReport: r.dateOfReport || '',
            createdBy: r.createdBy || '',
            createdAt: fmtDateTime(r.createdAt),
            filesCount: r.filesCount,
            photosCount: r.photosCount,
            downloaded: r.downloaded ? 'Yes' : 'No',
            downloadCount: r.downloadCount,
            lastDownloadAt: fmtDateTime(r.lastDownloadAt),
            lastDownloadBy: r.lastDownloadBy || '',
            lastFormat: r.lastDownloadFormat ? r.lastDownloadFormat.toUpperCase() : '',
        });

        const color = r.downloaded ? 'FFECFDF5' : 'FFFEF3F2';
        row.eachCell(cell => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: color } };
        });
    });

    details.autoFilter = {
        from: 'A1',
        to: { row: 1, column: details.columnCount },
    };

    return await wb.xlsx.writeBuffer();
};

// --------------------------------------------------------------
// Deterministic report ID helper (prevents duplicates at the
// Firestore level — same project + container = same doc ID).
// --------------------------------------------------------------
const buildReportDocId = (projectNo, containerNo) => {
    const cleanProject = String(projectNo || 'unknown')
        .trim()
        .replace(/[^a-z0-9]/gi, '_') || 'unknown';
    const cleanContainer = String(containerNo || 'unknown')
        .trim()
        .replace(/[^a-z0-9]/gi, '_') || 'unknown';
    return `${cleanProject}__${cleanContainer}`;
};

// --------------------------------------------------------------
// Firebase Storage & Firestore API service
// --------------------------------------------------------------
const reportApi = {
    getCurrentUser: () => {
        const user = auth.currentUser;
        if (user) {
            return {
                id: user.uid,
                name: user.displayName || user.email || 'Usuário',
                email: user.email || ''
            };
        }
        return null;
    },

    isAuthenticated: () => auth.currentUser !== null,

    getReports: async () => {
        if (!reportApi.isAuthenticated()) return [];
        try {
            const q = query(collection(db, 'tuv_reports'), orderBy('createdAt', 'desc'));
            const querySnapshot = await getDocs(q);
            return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        } catch (error) {
            console.error('Error getting reports:', error);
            return [];
        }
    },

    // --------------------------------------------------------------
    // Duplicate detection: a report is considered a duplicate when
    // the same projectNo + containerNo already exists.
    // --------------------------------------------------------------
    findExistingReport: async (projectNo, containerNo) => {
        if (!reportApi.isAuthenticated()) return null;

        const cleanProject = String(projectNo || '').trim();
        const cleanContainer = String(containerNo || '').trim();

        if (!cleanProject && !cleanContainer) return null;

        try {
            const q = query(collection(db, 'tuv_reports'));
            const snap = await getDocs(q);

            const match = snap.docs.find(docSnap => {
                const d = docSnap.data();
                return (
                    String(d.projectNo || '').trim() === cleanProject &&
                    String(d.containerNo || '').trim() === cleanContainer
                );
            });

            return match ? { id: match.id, ...match.data() } : null;
        } catch (error) {
            console.error('Erro ao verificar duplicatas:', error);
            return null;
        }
    },

    deleteReport: async (reportId) => {
        if (!reportApi.isAuthenticated()) throw new Error('Usuário não autenticado');

        const docSnap = await getDoc(doc(db, 'tuv_reports', reportId));
        if (docSnap.exists()) {
            const data = docSnap.data();
            const prefix = data.storagePrefix;

            if (prefix) {
                const deleteFolderRecursive = async (folderRef) => {
                    const listing = await listAll(folderRef);
                    await Promise.all(listing.items.map(item =>
                        deleteObject(item).catch(err => {
                            console.warn(`Falha ao deletar ${item.fullPath}:`, err.message);
                        })
                    ));
                    await Promise.all(listing.prefixes.map(sub =>
                        deleteFolderRecursive(sub)
                    ));
                };

                try {
                    const folderRef = ref(storage, prefix);
                    await deleteFolderRecursive(folderRef);
                } catch (err) {
                    console.warn('Erro ao apagar prefixo:', err.message);
                }
            } else {
                const allFiles = [...(data.files || []), ...(data.photos || [])];
                for (const file of allFiles) {
                    if (file.filePath) {
                        await deleteObject(ref(storage, file.filePath)).catch(() => { });
                    }
                }
            }
        }

        await deleteDoc(doc(db, 'tuv_reports', reportId));
        return true;
    },

    downloadReport: async (report, format = 'excel', onProgress = () => { }) => {
        const fetchAsBlob = async (fileUrl, filePath) => {
            if (fileUrl) {
                try {
                    const res = await fetch(fileUrl, { cache: 'force-cache' });
                    if (res.ok) return await res.blob();
                } catch {
                    // fall through to SDK
                }
            }
            const fileRef = ref(storage, filePath || fileUrl);
            return await getBlob(fileRef);
        };

        const blobToBase64 = (blob) =>
            new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    const dataUrl = reader.result;
                    resolve(dataUrl.split(',')[1]);
                };
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });

        const convertBlobToPdf = async (blob, fileName) => {
            const base64 = await blobToBase64(blob);
            const convertFunc = httpsCallable(functions, 'convertDocumentToPdf');
            const result = await convertFunc({ fileContent: base64, fileName });

            const byteCharacters = atob(result.data.pdfContent);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            return new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
        };

        try {
            const zip = new JSZip();

            let storagePrefix = report.storagePrefix;
            let zipRoot = report.zipRoot;

            if (!storagePrefix || !zipRoot) {
                const d = new Date(report.createdAt || Date.now());
                const year = d.getFullYear().toString();
                const month = (d.getMonth() + 1).toString().padStart(2, '0');
                const invoice = String(report.projectNo || 'unknown').replace(/[^a-z0-9]/gi, '_');
                const container = String(report.containerNo || 'unknown').replace(/[^a-z0-9]/gi, '_');
                const reportFolder = report.reportFolder || `Report_${invoice}_${container}`;

                storagePrefix = storagePrefix ||
                    `Reports/${year}/${month}/${reportFolder}/`;
                zipRoot = zipRoot || `${reportFolder}/`;
            }

            const allFiles = [
                ...(report.files || []),
                ...(report.photos || [])
            ];

            if (allFiles.length === 0) {
                throw new Error('Nenhum arquivo encontrado para download.');
            }

            const total = allFiles.length;
            let completed = 0;

            const filesNeedingPdf = format === 'pdf'
                ? allFiles.filter(f => /\.xlsx?$/i.test(f.fileName || ''))
                : [];
            const pdfTotal = filesNeedingPdf.length;

            const FETCH_CONCURRENCY = 8;

            const fetched = await mapWithConcurrency(
                allFiles,
                FETCH_CONCURRENCY,
                async (file) => {
                    const blob = await fetchAsBlob(file.fileUrl, file.filePath);
                    completed++;

                    const fetchShare = pdfTotal > 0 ? 0.7 : 0.95;
                    const percent = Math.round((completed / total) * 100 * fetchShare);

                    onProgress({
                        stage: 'fetching',
                        completed,
                        total,
                        percent,
                        currentFile: file.fileName || '',
                    });

                    return { file, blob };
                }
            );

            const PDF_CONCURRENCY = 3;
            let pdfCompleted = 0;

            const converted = await mapWithConcurrency(
                fetched,
                PDF_CONCURRENCY,
                async ({ file, blob }) => {
                    const isSpreadsheet = /\.xlsx?$/i.test(file.fileName || '');
                    if (format !== 'pdf' || !isSpreadsheet) {
                        return { file, blob };
                    }

                    const pdfBlob = await convertBlobToPdf(blob, file.fileName);
                    pdfCompleted++;

                    const percent = Math.round(
                        70 + (pdfCompleted / Math.max(1, pdfTotal)) * 25
                    );

                    onProgress({
                        stage: 'converting',
                        completed: pdfCompleted,
                        total: pdfTotal,
                        percent,
                        currentFile: file.fileName || '',
                    });

                    return { file, blob: pdfBlob };
                }
            );

            onProgress({ stage: 'zipping', completed: 0, total, percent: 95 });

            let filesCount = 0;
            let photosCount = 0;

            for (const { file, blob } of converted) {
                let finalFileName = file.fileName;
                let finalPath = file.filePath;

                if (file.type === 'photo') {
                    photosCount++;
                } else {
                    filesCount++;
                }

                if (format === 'pdf' && /\.xlsx?$/i.test(finalFileName)) {
                    finalFileName = finalFileName.replace(/\.xlsx?$/i, '.pdf');
                    finalPath = finalPath.replace(/\.xlsx?$/i, '.pdf');
                }

                const relative = finalPath.startsWith(storagePrefix)
                    ? finalPath.substring(storagePrefix.length)
                    : finalPath;

                zip.file(`${zipRoot}${relative}`, blob);
            }

            const zipBlob = await zip.generateAsync({ type: 'blob' });

            onProgress({ stage: 'saving', completed: 1, total: 1, percent: 100 });

            const invoice = String(report.projectNo || 'unknown').replace(/[^a-z0-9]/gi, '_');
            const container = String(report.containerNo || 'unknown').replace(/[^a-z0-9]/gi, '_');
            const zipName = `TUV_IR_SDS_${invoice}_${container}.zip`;
            saveAs(zipBlob, zipName);

            return {
                success: true,
                fileName: zipName,
                filesCount,
                photosCount
            };
        } catch (error) {
            console.error('Erro ao gerar ZIP:', error);
            throw error;
        }
    },

    logDownload: async (report, format, fileName, filesCount, photosCount) => {
        const user = auth.currentUser;

        const logEntry = {
            reportId: report.id,
            projectNo: report.projectNo || '',
            containerNo: report.containerNo || '',
            clientName: report.clientName || '',
            countryOfImport: report.countryOfImport || '',
            inspectorName: report.inspectorName || '',

            downloadedBy: user?.displayName || user?.email || 'Desconhecido',
            downloadedByEmail: user?.email || '',
            downloadedByUid: user?.uid || '',

            downloadedAt: new Date().toISOString(),
            format: format,
            fileName: fileName || '',
            filesCount: filesCount || 0,
            photosCount: photosCount || 0,
            reportFolder: report.reportFolder || ''
        };

        try {
            const docRef = await addDoc(collection(db, 'tuv_report_downloads'), logEntry);
            console.log('✅ Download registrado:', docRef.id, logEntry);
            return { id: docRef.id, ...logEntry };
        } catch (error) {
            console.error('❌ Falha ao registrar download:', error);
            return null;
        }
    },

    getDownloadLogs: async (filters = {}) => {
        if (!reportApi.isAuthenticated()) return [];
        try {
            const q = query(
                collection(db, 'tuv_report_downloads'),
                orderBy('downloadedAt', 'desc')
            );
            const snap = await getDocs(q);
            let logs = snap.docs.map(d => ({ id: d.id, ...d.data() }));

            if (filters.reportId) {
                logs = logs.filter(l => l.reportId === filters.reportId);
            }
            if (filters.userEmail) {
                logs = logs.filter(l => l.downloadedByEmail === filters.userEmail);
            }
            if (filters.startDate) {
                const start = new Date(filters.startDate).getTime();
                logs = logs.filter(l => new Date(l.downloadedAt).getTime() >= start);
            }
            if (filters.endDate) {
                const end = new Date(filters.endDate);
                end.setHours(23, 59, 59, 999);
                logs = logs.filter(l => new Date(l.downloadedAt).getTime() <= end.getTime());
            }
            return logs;
        } catch (error) {
            console.error('Erro ao carregar logs de download:', error);
            return [];
        }
    },

    getMonthlyReportData: async (year, month) => {
        if (!reportApi.isAuthenticated()) throw new Error('Usuário não autenticado');

        const allReports = await reportApi.getReports();

        const parseAnyDate = (raw) => {
            if (!raw) return null;
            let d = new Date(raw);
            if (!isNaN(d.getTime())) return d;
            const m = String(raw).match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
            if (m) {
                const yr = parseInt(m[3].length === 2 ? '20' + m[3] : m[3], 10);
                return new Date(yr, parseInt(m[2], 10) - 1, parseInt(m[1], 10));
            }
            return null;
        };

        const monthReports = allReports.filter(r => {
            const d = parseAnyDate(r.dateOfInspection) ||
                parseAnyDate(r.dateOfReport) ||
                parseAnyDate(r.createdAt);
            if (!d) return false;
            return d.getFullYear() === year && (d.getMonth() + 1) === month;
        });

        const q = query(collection(db, 'tuv_report_downloads'));
        const snap = await getDocs(q);
        const logsByReport = new Map();
        snap.docs.forEach(docSnap => {
            const log = { id: docSnap.id, ...docSnap.data() };
            if (!log.reportId) return;
            if (!logsByReport.has(log.reportId)) logsByReport.set(log.reportId, []);
            logsByReport.get(log.reportId).push(log);
        });

        return monthReports.map(r => {
            const logs = logsByReport.get(r.id) || [];
            logs.sort((a, b) =>
                new Date(b.downloadedAt).getTime() - new Date(a.downloadedAt).getTime()
            );
            const last = logs[0] || null;

            const filesCount = r.files?.length || 0;
            const photosCount = r.photos?.length || 0;

            return {
                ...r,
                downloaded: logs.length > 0,
                downloadCount: logs.length,
                lastDownloadAt: last?.downloadedAt || null,
                lastDownloadBy: last?.downloadedBy || null,
                lastDownloadFormat: last?.format || null,
                filesCount,
                photosCount,
            };
        }).sort((a, b) => {
            const da = parseAnyDate(a.dateOfInspection) || parseAnyDate(a.createdAt);
            const dbb = parseAnyDate(b.dateOfInspection) || parseAnyDate(b.createdAt);
            return (dbb?.getTime() || 0) - (da?.getTime() || 0);
        });
    },

    getDateRangeReportData: async (startDateStr, endDateStr) => {
        if (!reportApi.isAuthenticated()) throw new Error('Usuário não autenticado');

        const start = new Date(startDateStr);
        start.setHours(0, 0, 0, 0);
        const end = new Date(endDateStr);
        end.setHours(23, 59, 59, 999);

        if (isNaN(start.getTime()) || isNaN(end.getTime())) {
            throw new Error('Data inicial ou final inválida.');
        }
        if (start > end) {
            throw new Error('A data inicial não pode ser maior que a data final.');
        }

        const startMs = start.getTime();
        const endMs = end.getTime();

        const allReports = await reportApi.getReports();

        const rangeReports = allReports.filter(r => {
            if (!r.createdAt) return false;
            const t = new Date(r.createdAt).getTime();
            if (isNaN(t)) return false;
            return t >= startMs && t <= endMs;
        });

        const q = query(collection(db, 'tuv_report_downloads'));
        const snap = await getDocs(q);
        const logsByReport = new Map();
        snap.docs.forEach(docSnap => {
            const log = { id: docSnap.id, ...docSnap.data() };
            if (!log.reportId) return;
            if (!logsByReport.has(log.reportId)) logsByReport.set(log.reportId, []);
            logsByReport.get(log.reportId).push(log);
        });

        return rangeReports.map(r => {
            const logs = logsByReport.get(r.id) || [];
            logs.sort((a, b) =>
                new Date(b.downloadedAt).getTime() - new Date(a.downloadedAt).getTime()
            );
            const last = logs[0] || null;

            const filesCount = r.files?.length || 0;
            const photosCount = r.photos?.length || 0;

            return {
                ...r,
                downloaded: logs.length > 0,
                downloadCount: logs.length,
                lastDownloadAt: last?.downloadedAt || null,
                lastDownloadBy: last?.downloadedBy || null,
                lastDownloadFormat: last?.format || null,
                filesCount,
                photosCount,
            };
        }).sort((a, b) => {
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        });
    },
};

// --------------------------------------------------------------
// Skeleton Loader Component
// --------------------------------------------------------------
const SkeletonLoader = () => (
    <tbody>
        {[1, 2, 3, 4, 5].map(i => (
            <tr key={i} className={styles.skeletonRow}>
                <td><div className={styles.skeletonCell} style={{ width: '80px' }}></div></td>
                <td><div className={styles.skeletonCell} style={{ width: '80px' }}></div></td>
                <td><div className={styles.skeletonCell} style={{ width: '150px' }}></div></td>
                <td><div className={styles.skeletonCell} style={{ width: '120px' }}></div></td>
                <td><div className={styles.skeletonCell} style={{ width: '100px' }}></div></td>
                <td><div className={styles.skeletonCell} style={{ width: '100px' }}></div></td>
                <td><div className={styles.skeletonCell} style={{ width: '120px' }}></div></td>
                <td><div className={styles.skeletonCell} style={{ width: '100px' }}></div></td>
                <td><div className={styles.skeletonCell} style={{ width: '100px' }}></div></td>
                <td><div className={styles.skeletonCell} style={{ width: '100px' }}></div></td>
                <td><div className={styles.skeletonCell} style={{ width: '130px' }}></div></td>
            </tr>
        ))}
    </tbody>
);

// --------------------------------------------------------------
// Reports Table Component
// --------------------------------------------------------------
const ReportsTable = ({ reports, loading, onDownload, onDelete, onRefresh, onShowHistory }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const [searchTerm, setSearchTerm] = useState('');
    const [filterResult, setFilterResult] = useState('all');
    const [filterCountry, setFilterCountry] = useState('all');
    const [filterInspector, setFilterInspector] = useState('all');
    const [filterDateRange, setFilterDateRange] = useState({ start: '', end: '' });
    const [showFilters, setShowFilters] = useState(false);

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        try {
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) return String(dateStr);
            return date.toLocaleDateString('pt-BR') + ' ' + date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
        } catch { return String(dateStr); }
    };

    const safeString = (value, maxLength = null) => {
        if (value === undefined || value === null) return '-';
        let str = String(value);
        if (maxLength && str.length > maxLength) str = str.substring(0, maxLength - 3) + '...';
        return str;
    };

    const countryOptions = useMemo(() => {
        const countries = new Set();
        reports.forEach(r => {
            if (r.countryOfImport) countries.add(r.countryOfImport);
        });
        return ['all', ...Array.from(countries).sort()];
    }, [reports]);

    const inspectorOptions = useMemo(() => {
        const inspectors = new Set();
        reports.forEach(r => {
            if (r.inspectorName) inspectors.add(r.inspectorName);
        });
        return ['all', ...Array.from(inspectors).sort()];
    }, [reports]);

    const resultOptions = useMemo(() => {
        const results = new Set();
        reports.forEach(r => {
            if (r.inspectionResult) results.add(r.inspectionResult);
        });
        return ['all', ...Array.from(results).sort()];
    }, [reports]);

    const filteredReports = useMemo(() => {
        let filtered = [...reports];

        if (searchTerm.trim()) {
            const term = searchTerm.trim().toLowerCase();
            filtered = filtered.filter(r =>
                (r.projectNo && r.projectNo.toLowerCase().includes(term)) ||
                (r.unitName && r.unitName.toLowerCase().includes(term)) ||
                (r.clientName && r.clientName.toLowerCase().includes(term)) ||
                (r.clientNameExport && r.clientNameExport.toLowerCase().includes(term)) ||
                (r.countryOfImport && r.countryOfImport.toLowerCase().includes(term)) ||
                (r.inspectorName && r.inspectorName.toLowerCase().includes(term)) ||
                (r.createdBy && r.createdBy.toLowerCase().includes(term)) ||
                (r.containerNo && r.containerNo.toLowerCase().includes(term))
            );
        }

        if (filterResult !== 'all') {
            filtered = filtered.filter(r =>
                r.inspectionResult && r.inspectionResult.toLowerCase() === filterResult.toLowerCase()
            );
        }

        if (filterCountry !== 'all') {
            filtered = filtered.filter(r => r.countryOfImport === filterCountry);
        }

        if (filterInspector !== 'all') {
            filtered = filtered.filter(r => r.inspectorName === filterInspector);
        }

        if (filterDateRange.start) {
            const startDate = new Date(filterDateRange.start);
            startDate.setHours(0, 0, 0, 0);
            filtered = filtered.filter(r => {
                if (!r.createdAt) return false;
                const date = new Date(r.createdAt);
                return date >= startDate;
            });
        }
        if (filterDateRange.end) {
            const endDate = new Date(filterDateRange.end);
            endDate.setHours(23, 59, 59, 999);
            filtered = filtered.filter(r => {
                if (!r.createdAt) return false;
                const date = new Date(r.createdAt);
                return date <= endDate;
            });
        }

        return filtered;
    }, [reports, searchTerm, filterResult, filterCountry, filterInspector, filterDateRange]);

    const totalPages = Math.ceil(filteredReports.length / pageSize);
    const paginatedReports = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        const end = start + pageSize;
        return filteredReports.slice(start, end);
    }, [filteredReports, currentPage, pageSize]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filterResult, filterCountry, filterInspector, filterDateRange]);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const handlePageSizeChange = (e) => {
        setPageSize(Number(e.target.value));
        setCurrentPage(1);
    };

    const clearAllFilters = () => {
        setSearchTerm('');
        setFilterResult('all');
        setFilterCountry('all');
        setFilterInspector('all');
        setFilterDateRange({ start: '', end: '' });
        setCurrentPage(1);
    };

    const hasActiveFilters = searchTerm.trim() || filterResult !== 'all' || filterCountry !== 'all' || filterInspector !== 'all' || filterDateRange.start || filterDateRange.end;

    if (loading) {
        return (
            <div className={styles.tableContainer}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th style={{ width: '8%' }}>Status</th>
                            <th style={{ width: '8%' }}>Tipo</th>
                            <th style={{ width: '10%' }}>Projeto</th>
                            <th style={{ width: '14%' }}>Cliente</th>
                            <th style={{ width: '8%' }}>País</th>
                            <th style={{ width: '8%' }}>Container</th>
                            <th style={{ width: '10%' }}>Inspetor</th>
                            <th style={{ width: '10%' }}>Resultado</th>
                            <th style={{ width: '8%' }}>Data Inspeção</th>
                            <th style={{ width: '8%' }}>Data Relatório</th>
                            <th style={{ width: '14%' }}>Ações</th>
                        </tr>
                    </thead>
                    <SkeletonLoader />
                </table>
            </div>
        );
    }

    if (!reports || reports.length === 0) {
        return (
            <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>📄</div>
                <p>Nenhum relatório gerado ainda</p>
                <span>Clique em "Novo Relatório" para começar</span>
            </div>
        );
    }

    return (
        <div>
            <div className={styles.filterBar} style={{
                background: '#fff',
                borderRadius: '12px',
                padding: '16px 20px',
                marginBottom: '16px',
                border: '1px solid #e9ecef',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}>
                <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '12px',
                    alignItems: 'center'
                }}>
                    <div style={{ flex: '1', minWidth: '200px' }}>
                        <input
                            type="text"
                            placeholder="Buscar por projeto, cliente, container, inspetor..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '8px 14px',
                                border: '2px solid #e2e8f0',
                                borderRadius: '8px',
                                fontSize: '14px',
                                transition: 'all 0.2s',
                                outline: 'none',
                                background: '#f8fafc'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#0d47a1'}
                            onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                        />
                    </div>

                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        style={{
                            padding: '8px 16px',
                            background: showFilters ? '#0d47a1' : '#f1f5f9',
                            color: showFilters ? 'white' : '#475569',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: 500,
                            transition: 'all 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {showFilters ? '▲ Ocultar Filtros' : '▼ Mostrar Filtros'}
                        {hasActiveFilters && (
                            <span style={{
                                background: '#0d47a1',
                                color: 'white',
                                borderRadius: '50%',
                                width: '18px',
                                height: '18px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '11px',
                                fontWeight: 'bold'
                            }}>
                                {filteredReports.length}
                            </span>
                        )}
                    </button>

                    {hasActiveFilters && (
                        <button
                            onClick={clearAllFilters}
                            style={{
                                padding: '8px 14px',
                                background: 'transparent',
                                color: '#dc2626',
                                border: '1px solid #dc2626',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontWeight: 500,
                                transition: 'all 0.2s',
                                whiteSpace: 'nowrap'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.background = '#fee2e2';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.background = 'transparent';
                            }}
                        >
                            ✕ Limpar Filtros
                        </button>
                    )}

                    <span style={{
                        fontSize: '13px',
                        color: '#64748b',
                        marginLeft: 'auto',
                        whiteSpace: 'nowrap'
                    }}>
                        {filteredReports.length} {filteredReports.length === 1 ? 'resultado' : 'resultados'}
                    </span>
                </div>

                {showFilters && (
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                        gap: '16px',
                        marginTop: '16px',
                        paddingTop: '16px',
                        borderTop: '1px solid #e9ecef'
                    }}>
                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: '12px',
                                fontWeight: 600,
                                color: '#64748b',
                                marginBottom: '4px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}>
                                Resultado
                            </label>
                            <select
                                value={filterResult}
                                onChange={(e) => setFilterResult(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '8px 12px',
                                    border: '2px solid #e2e8f0',
                                    borderRadius: '8px',
                                    fontSize: '13px',
                                    outline: 'none',
                                    background: '#f8fafc'
                                }}
                            >
                                {resultOptions.map(opt => (
                                    <option key={opt} value={opt}>
                                        {opt === 'all' ? 'Todos' : opt}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: '12px',
                                fontWeight: 600,
                                color: '#64748b',
                                marginBottom: '4px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}>
                                País
                            </label>
                            <select
                                value={filterCountry}
                                onChange={(e) => setFilterCountry(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '8px 12px',
                                    border: '2px solid #e2e8f0',
                                    borderRadius: '8px',
                                    fontSize: '13px',
                                    outline: 'none',
                                    background: '#f8fafc'
                                }}
                            >
                                {countryOptions.map(opt => (
                                    <option key={opt} value={opt}>
                                        {opt === 'all' ? 'Todos' : opt}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: '12px',
                                fontWeight: 600,
                                color: '#64748b',
                                marginBottom: '4px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}>
                                Inspetor
                            </label>
                            <select
                                value={filterInspector}
                                onChange={(e) => setFilterInspector(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '8px 12px',
                                    border: '2px solid #e2e8f0',
                                    borderRadius: '8px',
                                    fontSize: '13px',
                                    outline: 'none',
                                    background: '#f8fafc'
                                }}
                            >
                                {inspectorOptions.map(opt => (
                                    <option key={opt} value={opt}>
                                        {opt === 'all' ? 'Todos' : opt}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: '12px',
                                fontWeight: 600,
                                color: '#64748b',
                                marginBottom: '4px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}>
                                Data Início
                            </label>
                            <input
                                type="date"
                                value={filterDateRange.start}
                                onChange={(e) => setFilterDateRange(prev => ({ ...prev, start: e.target.value }))}
                                style={{
                                    width: '100%',
                                    padding: '8px 12px',
                                    border: '2px solid #e2e8f0',
                                    borderRadius: '8px',
                                    fontSize: '13px',
                                    outline: 'none',
                                    background: '#f8fafc'
                                }}
                            />
                        </div>

                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: '12px',
                                fontWeight: 600,
                                color: '#64748b',
                                marginBottom: '4px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}>
                                Data Fim
                            </label>
                            <input
                                type="date"
                                value={filterDateRange.end}
                                onChange={(e) => setFilterDateRange(prev => ({ ...prev, end: e.target.value }))}
                                style={{
                                    width: '100%',
                                    padding: '8px 12px',
                                    border: '2px solid #e2e8f0',
                                    borderRadius: '8px',
                                    fontSize: '13px',
                                    outline: 'none',
                                    background: '#f8fafc'
                                }}
                            />
                        </div>
                    </div>
                )}
            </div>

            <div className={styles.tableContainer}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th style={{ width: '7%' }}>Container</th>
                            <th style={{ width: '9%' }}>Invoice</th>
                            <th style={{ width: '12%' }}>Client (Exporter)</th>
                            <th style={{ width: '12%' }}>Client (Importer)</th>
                            <th style={{ width: '7%' }}>Country</th>
                            <th style={{ width: '11%' }}>SIF - Unit</th>
                            <th style={{ width: '9%' }}>Inspector</th>
                            <th style={{ width: '9%' }}>Result</th>
                            <th style={{ width: '7%' }}>Date of Inspection</th>
                            <th style={{ width: '7%' }}>Report Date</th>
                            <th style={{ width: '10%' }}>Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedReports.length === 0 ? (
                            <tr>
                                <td colSpan="11" style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                                    Nenhum relatório encontrado com os filtros selecionados
                                </td>
                            </tr>
                        ) : (
                            paginatedReports.map(report => {
                                const result = report.inspectionResult || '';
                                const resultColor = result.toLowerCase() === 'satisfactory' ? '#16a34a' :
                                    result.toLowerCase() === 'unsatisfactory' ? '#dc2626' : '#f59e0b';
                                const resultBg = result.toLowerCase() === 'satisfactory' ? '#dcfce7' :
                                    result.toLowerCase() === 'unsatisfactory' ? '#fee2e2' : '#fef3c7';

                                return (
                                    <tr key={report.id} className={styles.tableRow}>
                                        <td>{safeString(report.containerNo, 15)}</td>
                                        <td title={safeString(report.projectNo)}>
                                            <strong>{safeString(report.projectNo, 20)}</strong>
                                        </td>
                                        <td title={safeString(report.clientNameExport)}>
                                            {safeString(report.clientNameExport, 25)}
                                        </td>
                                        <td title={safeString(report.clientName)}>
                                            {safeString(report.clientName, 25)}
                                        </td>
                                        <td>{safeString(report.countryOfImport)}</td>
                                        <td title={safeString(report.unitName)}>
                                            {safeString(report.unitName, 25)}
                                        </td>
                                        <td>{safeString(report.inspectorName)}</td>
                                        <td>
                                            {result ? (
                                                <span style={{
                                                    display: 'inline-block',
                                                    padding: '2px 12px',
                                                    borderRadius: '20px',
                                                    fontSize: '12px',
                                                    fontWeight: 600,
                                                    background: resultBg,
                                                    color: resultColor
                                                }}>
                                                    {result}
                                                </span>
                                            ) : '-'}
                                        </td>
                                        <td>{formatDate(report.dateOfInspection)}</td>
                                        <td>{formatDate(report.dateOfReport || report.createdAt)}</td>
                                        <td>
                                            <div className={styles.actionsContainer}>
                                                <button
                                                    onClick={() => onDownload(report)}
                                                    className={`${styles.actionButton} ${styles.downloadButton}`}
                                                    title="Baixar (Excel ou PDF)"
                                                >
                                                    <HiOutlineDocumentDownload size={14} />
                                                </button>
                                                <button
                                                    onClick={() => onShowHistory(report)}
                                                    className={`${styles.actionButton}`}
                                                    style={{ background: '#eff6ff', color: '#1e40af' }}
                                                    title="Ver histórico de downloads"
                                                >
                                                    <HiOutlineClock size={14} />
                                                </button>
                                                <button
                                                    onClick={() => onDelete(report.id)}
                                                    className={`${styles.actionButton} ${styles.deleteButton}`}
                                                    title="Excluir"
                                                >
                                                    <HiOutlineTrash size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {filteredReports.length > 0 && (
                <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '16px 20px',
                    background: '#fff',
                    borderRadius: '12px',
                    border: '1px solid #e9ecef',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                    marginTop: '16px',
                    gap: '12px'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        flexWrap: 'wrap'
                    }}>
                        <span style={{ fontSize: '14px', color: '#64748b' }}>
                            Mostrando {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, filteredReports.length)} de {filteredReports.length}
                        </span>
                        <select
                            value={pageSize}
                            onChange={handlePageSizeChange}
                            style={{
                                padding: '6px 10px',
                                border: '2px solid #e2e8f0',
                                borderRadius: '8px',
                                fontSize: '13px',
                                outline: 'none',
                                background: '#f8fafc'
                            }}
                        >
                            <option value={5}>5 por página</option>
                            <option value={10}>10 por página</option>
                            <option value={25}>25 por página</option>
                            <option value={50}>50 por página</option>
                        </select>
                    </div>

                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                    }}>
                        <button
                            onClick={() => handlePageChange(1)}
                            disabled={currentPage === 1}
                            style={{
                                padding: '6px 12px',
                                border: '1px solid #e2e8f0',
                                borderRadius: '6px',
                                background: currentPage === 1 ? '#f1f5f9' : 'white',
                                color: currentPage === 1 ? '#94a3b8' : '#475569',
                                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                fontSize: '13px',
                                transition: 'all 0.2s'
                            }}
                        >
                            «
                        </button>
                        <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            style={{
                                padding: '6px 12px',
                                border: '1px solid #e2e8f0',
                                borderRadius: '6px',
                                background: currentPage === 1 ? '#f1f5f9' : 'white',
                                color: currentPage === 1 ? '#94a3b8' : '#475569',
                                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                fontSize: '13px',
                                transition: 'all 0.2s'
                            }}
                        >
                            ‹
                        </button>

                        <span style={{
                            padding: '6px 16px',
                            fontSize: '14px',
                            fontWeight: 600,
                            color: '#0d47a1',
                            background: '#eff6ff',
                            borderRadius: '6px',
                            minWidth: '40px',
                            textAlign: 'center'
                        }}>
                            {currentPage} / {totalPages}
                        </span>

                        <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            style={{
                                padding: '6px 12px',
                                border: '1px solid #e2e8f0',
                                borderRadius: '6px',
                                background: currentPage === totalPages ? '#f1f5f9' : 'white',
                                color: currentPage === totalPages ? '#94a3b8' : '#475569',
                                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                                fontSize: '13px',
                                transition: 'all 0.2s'
                            }}
                        >
                            ›
                        </button>
                        <button
                            onClick={() => handlePageChange(totalPages)}
                            disabled={currentPage === totalPages}
                            style={{
                                padding: '6px 12px',
                                border: '1px solid #e2e8f0',
                                borderRadius: '6px',
                                background: currentPage === totalPages ? '#f1f5f9' : 'white',
                                color: currentPage === totalPages ? '#94a3b8' : '#475569',
                                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                                fontSize: '13px',
                                transition: 'all 0.2s'
                            }}
                        >
                            »
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

// --------------------------------------------------------------
// Auth Guard Component
// --------------------------------------------------------------
const AuthGuard = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [checking, setChecking] = useState(true);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            setIsAuthenticated(!!user);
            setChecking(false);
        });
        return () => unsubscribe();
    }, []);

    if (checking) return <div className={styles.loadingContainer}>Verificando autenticação...</div>;

    if (!isAuthenticated) {
        return (
            <div className={styles.authErrorContainer}>
                <div className={styles.authErrorIcon}>🔒</div>
                <h3>Você precisa estar logado para acessar esta página</h3>
                <p>Por favor, faça login no sistema para gerar e gerenciar relatórios TÜV.</p>
                <button onClick={() => window.location.href = '/login'} className={styles.primaryButton}>Ir para Login</button>
            </div>
        );
    }

    return children;
};

// --------------------------------------------------------------
// Download History Modal Component
// --------------------------------------------------------------
const DownloadHistoryModal = ({ report, onClose }) => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        (async () => {
            setLoading(true);
            const history = await reportApi.getDownloadLogs({ reportId: report.id });
            if (mounted) {
                setLogs(history);
                setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, [report.id]);

    const formatDateTime = (iso) => {
        if (!iso) return '-';
        try {
            const d = new Date(iso);
            return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
        } catch { return iso; }
    };

    return (
        <div className={styles.modalBackdrop} onClick={onClose}>
            <div
                className={styles.formContainer}
                style={{ maxWidth: '720px', padding: '24px 28px', borderRadius: '16px' }}
                onClick={(e) => e.stopPropagation()}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                    <div>
                        <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#1e293b' }}>
                            📥 Histórico de Downloads
                        </h3>
                        <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '13px' }}>
                            {report.projectNo || '-'} · {report.clientName || '-'} · Container {report.containerNo || '-'}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: '#f1f5f9',
                            border: 'none',
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            fontSize: '20px',
                            cursor: 'pointer',
                            color: '#64748b',
                            fontWeight: 300
                        }}
                    >
                        ×
                    </button>
                </div>

                {loading ? (
                    <p style={{ textAlign: 'center', color: '#64748b', padding: '20px' }}>Carregando...</p>
                ) : logs.length === 0 ? (
                    <div style={{
                        textAlign: 'center',
                        padding: '40px 20px',
                        background: '#f8fafc',
                        borderRadius: '12px',
                        color: '#94a3b8'
                    }}>
                        <div style={{ fontSize: '36px', marginBottom: '8px' }}>📭</div>
                        <p style={{ margin: 0 }}>Nenhum download registrado para este relatório</p>
                    </div>
                ) : (
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                            <thead>
                                <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                                    <th style={{ padding: '10px 12px', fontWeight: 600, color: '#475569' }}>Usuário</th>
                                    <th style={{ padding: '10px 12px', fontWeight: 600, color: '#475569' }}>Formato</th>
                                    <th style={{ padding: '10px 12px', fontWeight: 600, color: '#475569' }}>Arquivos</th>
                                    <th style={{ padding: '10px 12px', fontWeight: 600, color: '#475569' }}>Data/Hora</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.map(log => (
                                    <tr key={log.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '10px 12px' }}>
                                            <div style={{ fontWeight: 600, color: '#1e293b' }}>{log.downloadedBy}</div>
                                            <div style={{ fontSize: '11px', color: '#94a3b8' }}>{log.downloadedByEmail}</div>
                                        </td>
                                        <td style={{ padding: '10px 12px' }}>
                                            <span style={{
                                                padding: '2px 10px',
                                                borderRadius: '20px',
                                                fontSize: '11px',
                                                fontWeight: 600,
                                                background: log.format === 'pdf' ? '#fee2e2' : '#dbeafe',
                                                color: log.format === 'pdf' ? '#991b1b' : '#1e40af'
                                            }}>
                                                {log.format?.toUpperCase()}
                                            </span>
                                        </td>
                                        <td style={{ padding: '10px 12px', color: '#475569' }}>
                                            {log.filesCount} doc(s) · {log.photosCount} foto(s)
                                        </td>
                                        <td style={{ padding: '10px 12px', color: '#475569' }}>
                                            {formatDateTime(log.downloadedAt)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

// --------------------------------------------------------------
// Download Progress Bar (inline component)
// --------------------------------------------------------------
const DownloadProgress = ({ progress, format }) => {
    const percent = Math.max(0, Math.min(100, progress?.percent || 0));
    const stage = progress?.stage || 'starting';

    const stageLabel = {
        starting: 'Preparando...',
        fetching: 'Baixando arquivos',
        converting: 'Convertendo para PDF',
        zipping: 'Compactando arquivos',
        saving: 'Salvando arquivo',
    }[stage] || 'Processando...';

    const stageEmoji = {
        starting: '⏳',
        fetching: '⬇️',
        converting: '📄',
        zipping: '🗜️',
        saving: '💾',
    }[stage] || '⏳';

    const detail = (() => {
        if (!progress) return '';
        if (stage === 'fetching' && progress.total) {
            return `${progress.completed}/${progress.total} arquivos`;
        }
        if (stage === 'converting' && progress.total) {
            return `${progress.completed}/${progress.total} planilhas`;
        }
        return '';
    })();

    return (
        <div style={{ marginTop: '20px' }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px',
                fontSize: '13px',
                fontWeight: 500,
                color: '#1e293b'
            }}>
                <span>
                    {stageEmoji} {stageLabel}{detail ? ` · ${detail}` : ''}
                </span>
                <span style={{ color: '#0d47a1', fontWeight: 700 }}>{percent}%</span>
            </div>

            <div style={{
                width: '100%',
                height: '10px',
                background: '#e2e8f0',
                borderRadius: '999px',
                overflow: 'hidden',
                position: 'relative'
            }}>
                <div style={{
                    width: `${percent}%`,
                    height: '100%',
                    background: format === 'pdf'
                        ? 'linear-gradient(90deg, #b91c1c, #ef4444)'
                        : 'linear-gradient(90deg, #0d47a1, #3b82f6)',
                    borderRadius: '999px',
                    transition: 'width 220ms ease-out',
                    boxShadow: '0 0 10px rgba(13,71,161,0.25)'
                }} />
            </div>

            {progress?.currentFile && (
                <p style={{
                    margin: '8px 0 0 0',
                    fontSize: '11px',
                    color: '#94a3b8',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                }} title={progress.currentFile}>
                    {progress.currentFile}
                </p>
            )}
        </div>
    );
};

// --------------------------------------------------------------
// Main Component
// --------------------------------------------------------------
const TUVReportPage = () => {
    const [extractedData, setExtractedData] = useState({});
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [status, setStatus] = useState({ message: '', type: '' });
    const [evaluationId, setEvaluationId] = useState('');
    const [reports, setReports] = useState([]);
    const [reportsLoading, setReportsLoading] = useState(true);
    const [currentUser, setCurrentUser] = useState(null);
    const [searchMode, setSearchMode] = useState('list');

    // Guards against double-firing generateReport (double-click, fast Enter, etc.)
    const generatingRef = useRef(false);

    // Download modal state
    const [showDownloadModal, setShowDownloadModal] = useState(false);
    const [reportToDownload, setReportToDownload] = useState(null);
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadProgress, setDownloadProgress] = useState(null);
    const [downloadFormat, setDownloadFormat] = useState('excel');

    // Download history modal state
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [reportToViewHistory, setReportToViewHistory] = useState(null);

    // Monthly report modal state
    const [showMonthlyModal, setShowMonthlyModal] = useState(false);
    const [monthlyYear, setMonthlyYear] = useState(new Date().getFullYear());
    const [monthlyMonth, setMonthlyMonth] = useState(new Date().getMonth() + 1);
    const [isGeneratingMonthly, setIsGeneratingMonthly] = useState(false);

    // Date range report modal state
    const [showRangeModal, setShowRangeModal] = useState(false);
    const [rangeStartDate, setRangeStartDate] = useState('');
    const [rangeEndDate, setRangeEndDate] = useState('');
    const [isGeneratingRange, setIsGeneratingRange] = useState(false);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(setCurrentUser);
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (currentUser) loadReports();
    }, [currentUser]);

    const loadReports = async () => {
        setReportsLoading(true);
        try {
            const loadedReports = await reportApi.getReports();
            setReports(loadedReports);
        } catch (error) {
            console.error('Error loading reports:', error);
            setStatus({ message: 'Erro ao carregar relatórios', type: 'error' });
            setTimeout(() => setStatus({ message: '', type: '' }), 3000);
        } finally {
            setReportsLoading(false);
        }
    };

    const fetchAndExtractData = useCallback(async (id, forceRefresh = false) => {
        setLoading(true);

        try {
            const apiData = await fetchEvaluationData(id, forceRefresh);
            const parsedData = parseNewApiFormat(apiData);

            const groups = groupSamplingRowsByDate(parsedData.samplingRows || []);
            parsedData.samplingGroupsCount = groups.length;

            setExtractedData(parsedData);
            setIsDataLoaded(true);
            setStatus({ message: `✅ Dados carregados! ${parsedData.perishableProducts?.length || 0} produto(s) encontrado(s). ${groups.length} grupo(s) de amostragem.`, type: 'success' });
            setTimeout(() => setStatus({ message: '', type: '' }), 3000);
            return parsedData;
        } catch (error) {
            console.error('Error fetching evaluation:', error);
            setStatus({ message: error.message || 'Erro ao buscar dados da avaliação', type: 'error' });
            setTimeout(() => setStatus({ message: '', type: '' }), 5000);
            throw error;
        } finally {
            setLoading(false);
        }
    }, []);

    const handleSelectPending = async (pendingDocId) => {
        try {
            setLoading(true);
            setStatus({ message: '⏳ Buscando dados completos via API da Checklist Fácil...', type: 'info' });

            const firestoreData = await webhookService.getEvaluationData(pendingDocId);
            const realEvaluationId = firestoreData?.evaluationId || pendingDocId;

            if (!realEvaluationId) {
                throw new Error('ID da avaliação não encontrado nos dados do Firestore.');
            }

            await fetchAndExtractData(realEvaluationId, false);
            await webhookService.markAsProcessed(pendingDocId);

        } catch (error) {
            console.error('Erro ao carregar avaliação automática:', error);
            setStatus({ message: error.message || 'Erro ao carregar dados da avaliação', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const generateReport = useCallback(async () => {
        // ---- Guard 1: reentrancy lock (double-click / fast Enter) ----
        if (generatingRef.current) {
            console.warn('generateReport already running — ignoring duplicate invocation.');
            return;
        }

        if (!currentUser) {
            setStatus({ message: 'Você precisa estar logado para gerar relatórios', type: 'error' });
            return;
        }

        if (!extractedData.projectNo && !extractedData.clientName) {
            setStatus({ message: 'Nenhum dado carregado. Por favor, busque uma avaliação primeiro.', type: 'error' });
            return;
        }

        generatingRef.current = true;
        setLoading(true);

        try {
            // ---- Guard 2: check for an existing report for this project+container ----
            setStatus({ message: '🔎 Verificando duplicatas...', type: 'info' });

            let existingReport = null;
            try {
                existingReport = await reportApi.findExistingReport(
                    extractedData.projectNo,
                    extractedData.containerNo
                );
            } catch (checkErr) {
                console.warn('Falha ao verificar duplicata (prosseguindo):', checkErr);
            }

            if (existingReport) {
                const createdAt = existingReport.createdAt
                    ? new Date(existingReport.createdAt).toLocaleString('pt-BR')
                    : 'data desconhecida';

                const proceed = window.confirm(
                    `⚠️ Já existe um relatório para este projeto/container.\n\n` +
                    `Projeto: ${extractedData.projectNo || '-'}\n` +
                    `Container: ${extractedData.containerNo || '-'}\n` +
                    `Criado por: ${existingReport.createdBy || '-'}\n` +
                    `Criado em: ${createdAt}\n\n` +
                    `Deseja substituir o relatório existente?\n` +
                    `(O relatório antigo e seus arquivos serão removidos.)`
                );

                if (!proceed) {
                    setStatus({
                        message: 'Geração cancelada — relatório duplicado evitado.',
                        type: 'info'
                    });
                    setTimeout(() => setStatus({ message: '', type: '' }), 4000);
                    return;
                }

                // User confirmed → delete the old one (also purges Storage)
                setStatus({ message: '🗑️ Removendo relatório anterior...', type: 'info' });
                await reportApi.deleteReport(existingReport.id);
            }

            setStatus({ message: '📊 Gerando relatórios...', type: 'info' });

            const user = auth.currentUser;
            if (!user) throw new Error('Usuário não autenticado');

            const timestamp = new Date();
            const year = timestamp.getFullYear().toString();
            const month = (timestamp.getMonth() + 1).toString().padStart(2, '0');
            const invoice = String(extractedData.projectNo || 'unknown').replace(/[^a-z0-9]/gi, '_');
            const container = String(extractedData.containerNo || 'unknown').replace(/[^a-z0-9]/gi, '_');

            const reportFolder = `Report_${invoice}_${container}`;
            const storagePrefix = `Reports/${year}/${month}/${reportFolder}/`;
            const zipRoot = `${reportFolder}/`;

            const attachments = [];

            const inspectionBuffer = await generateInspectionXlsx(extractedData);
            const inspectionBlob = new Blob([inspectionBuffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            attachments.push({
                type: 'inspection',
                blob: inspectionBlob
            });

            const samplingWorkbooks = await generateSamplingDataSheetWorkbooks(extractedData);
            const samplingGroupsCount = samplingWorkbooks.length;
            extractedData.samplingGroupsCount = samplingGroupsCount;

            for (const { date, wb } of samplingWorkbooks) {
                const samplingBuffer = await wb.xlsx.writeBuffer();
                attachments.push({
                    type: 'sampling',
                    dateLabel: dateLabelForFileName(date),
                    blob: new Blob([samplingBuffer], {
                        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                    })
                });
            }

            const uploadedFiles = [];

            for (const attachment of attachments) {
                const subfolder = attachment.type === 'sampling'
                    ? 'Sampling_Data_Sheet'
                    : 'Inspection_Report';

                const dateSuffix = attachment.dateLabel ? `_${attachment.dateLabel}` : '';
                const fileName = `TUV_${subfolder}_${invoice}${dateSuffix}_${timestamp.getTime()}.xlsx`;
                const storagePath = `${storagePrefix}${subfolder}/${fileName}`;

                const storageRef = ref(storage, storagePath);
                await uploadBytes(storageRef, attachment.blob, {
                    contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });
                const downloadURL = await getDownloadURL(storageRef);

                uploadedFiles.push({
                    type: attachment.type,
                    fileName,
                    filePath: storagePath,
                    fileUrl: downloadURL,
                    fileSize: attachment.blob.size
                });
            }

            const photoAttachments = extractedData.photoAttachments || [];
            const uploadedPhotos = [];
            let photosFailed = 0;

            if (photoAttachments.length > 0) {
                setStatus({
                    message: `📸 Baixando 0/${photoAttachments.length} fotos anexadas...`,
                    type: 'info'
                });

                const { results, failed } = await fetchPhotoAttachments(
                    photoAttachments,
                    (done, total) => setStatus({
                        message: `📸 Baixando ${done}/${total} fotos anexadas...`,
                        type: 'info'
                    })
                );

                for (const { path: relativePath, blob } of results) {
                    const storagePath = `${storagePrefix}${relativePath}`;

                    const photoRef = ref(storage, storagePath);
                    await uploadBytes(photoRef, blob, {
                        contentType: blob.type || 'image/jpeg'
                    });
                    const photoUrl = await getDownloadURL(photoRef);

                    uploadedPhotos.push({
                        type: 'photo',
                        fileName: relativePath.split('/').pop(),
                        filePath: storagePath,
                        fileUrl: photoUrl,
                        fileSize: blob.size
                    });
                }

                photosFailed = failed.length;

                if (failed.length > 0) {
                    console.warn(`${failed.length} foto(s) não puderam ser baixadas:`, failed);
                }
            }

            const reportRecord = {
                documentType: 'combined',
                projectNo: String(extractedData.projectNo || ''),
                unitName: String(extractedData.unitName || ''),
                clientName: String(extractedData.clientName || ''),
                clientNameExport: String(extractedData.clientNameExport || ''),
                countryOfImport: String(extractedData.countryOfImport || ''),
                inspectorName: String(extractedData.inspectorName || ''),
                dateOfInspection: extractedData.dateOfInspection || null,
                dateOfReport: extractedData.dateOfReport || null,
                inspectionResult: String(extractedData.inspectionResult || ''),
                containerNo: String(extractedData.containerNo || ''),
                placeOfInspection: String(extractedData.placeOfInspection || ''),

                reportFolder,
                storagePrefix,
                zipRoot,

                files: uploadedFiles,
                photos: uploadedPhotos,

                createdBy: user.displayName || user.email || 'Usuário',
                createdByEmail: user.email || '',
                createdByUid: user.uid,
                createdAt: timestamp.toISOString(),

                metadata: {
                    clientAddress: String(extractedData.clientAddress || ''),
                    applicableAirCaiNumber: String(extractedData.applicableAirCaiNumber || ''),
                    totalTime: String(extractedData.totalTime || ''),
                    conditionOfGoods: String(extractedData.conditionOfGoods || ''),
                    shipmentType: String(extractedData.shipmentType || ''),
                    boardingType: String(extractedData.boardingType || ''),
                    containerType: String(extractedData.containerType || ''),
                    productsCount: (extractedData.perishableProducts || []).length,
                    samplingGroups: samplingGroupsCount,
                    filesCount: uploadedFiles.length,
                    photosCount: uploadedPhotos.length,
                    photosFailed
                }
            };

            // ---- Guard 3: deterministic doc ID — Firestore rejects dupes at
            // the write level even if the check above was bypassed.
            const reportDocId = buildReportDocId(
                extractedData.projectNo,
                extractedData.containerNo
            );
            await setDoc(doc(db, 'tuv_reports', reportDocId), reportRecord, { merge: false });

            await loadReports();

            const photosMessage = uploadedPhotos.length > 0
                ? ` ${uploadedPhotos.length} foto(s) armazenada(s) em ${storagePrefix}Fotos/.${photosFailed > 0 ? ` (${photosFailed} falharam)` : ''}`
                : '';

            setStatus({
                message: `✅ Relatório gerado com sucesso! ${samplingGroupsCount} grupo(s) de amostragem.${photosMessage}`,
                type: 'success'
            });

            setTimeout(() => {
                setStatus({ message: '', type: '' });
                setIsModalOpen(false);
                setEvaluationId('');
                setExtractedData({});
                setIsDataLoaded(false);
                setSearchMode('list');
            }, 2000);

        } catch (error) {
            console.error('Generate report error:', error);
            setStatus({ message: `Erro: ${error.message}`, type: 'error' });
        } finally {
            generatingRef.current = false;
            setLoading(false);
        }
    }, [extractedData, currentUser]);

    const handleFetchEvaluation = async () => {
        if (!evaluationId || evaluationId.trim() === '') {
            setStatus({ message: 'Por favor, informe o ID da avaliação', type: 'error' });
            return;
        }
        await fetchAndExtractData(evaluationId.trim(), false);
    };

    const openModal = () => {
        setExtractedData({});
        setEvaluationId('');
        setIsDataLoaded(false);
        setSearchMode('list');
        setStatus({ message: '', type: '' });
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setExtractedData({});
        setEvaluationId('');
        setIsDataLoaded(false);
        setSearchMode('list');
        setStatus({ message: '', type: '' });
    };

    // ---- Download flow ----
    const handleDownloadClick = (report) => {
        setReportToDownload(report);
        setDownloadProgress(null);
        setDownloadFormat('excel');
        setShowDownloadModal(true);
    };

    const handleConfirmDownload = async (format) => {
        if (!reportToDownload) return;
        setIsDownloading(true);
        setDownloadFormat(format);
        setDownloadProgress({ stage: 'starting', completed: 0, total: 0, percent: 0 });
        setStatus({ message: '📥 Preparando arquivos...', type: 'info' });

        try {
            const result = await reportApi.downloadReport(
                reportToDownload,
                format,
                (progress) => setDownloadProgress(progress)
            );

            await reportApi.logDownload(
                reportToDownload,
                format,
                result?.fileName || '',
                result?.filesCount || 0,
                result?.photosCount || 0
            );

            setStatus({ message: '📥 Download concluído!', type: 'success' });
        } catch (error) {
            console.error('Download error:', error);
            setStatus({ message: `Erro ao baixar: ${error.message}`, type: 'error' });
        } finally {
            setIsDownloading(false);
            setTimeout(() => {
                setShowDownloadModal(false);
                setReportToDownload(null);
                setDownloadProgress(null);
                setTimeout(() => setStatus({ message: '', type: '' }), 500);
            }, 600);
        }
    };

    const handleDeleteReport = async (reportId) => {
        if (window.confirm('Tem certeza que deseja excluir este relatório? Todos os arquivos em Storage serão removidos.')) {
            try {
                await reportApi.deleteReport(reportId);
                await loadReports();
                setStatus({ message: '🗑️ Relatório excluído com sucesso!', type: 'success' });
            } catch (error) {
                console.error('Delete error:', error);
                setStatus({ message: `Erro ao excluir: ${error.message}`, type: 'error' });
            }
            setTimeout(() => setStatus({ message: '', type: '' }), 2000);
        }
    };

    const handleShowHistory = (report) => {
        setReportToViewHistory(report);
        setShowHistoryModal(true);
    };

    // ---- Monthly report flow ----
    const handleGenerateMonthlyReport = async () => {
        setIsGeneratingMonthly(true);
        setStatus({ message: '⏳ Buscando relatórios do mês...', type: 'info' });
        try {
            const data = await reportApi.getMonthlyReportData(monthlyYear, monthlyMonth);

            if (data.length === 0) {
                setStatus({
                    message: `Nenhum relatório encontrado para ${String(monthlyMonth).padStart(2, '0')}/${monthlyYear}.`,
                    type: 'error'
                });
                setIsGeneratingMonthly(false);
                setTimeout(() => setStatus({ message: '', type: '' }), 4000);
                return;
            }

            setStatus({ message: '📊 Gerando planilha...', type: 'info' });

            const buffer = await generateMonthlyReportXlsx(data, monthlyYear, monthlyMonth);
            const blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const m = String(monthlyMonth).padStart(2, '0');
            saveAs(blob, `TUV_Monthly_Report_${monthlyYear}_${m}.xlsx`);

            setStatus({
                message: `✅ Relatório mensal gerado com ${data.length} relatório(s).`,
                type: 'success'
            });
            setShowMonthlyModal(false);
        } catch (err) {
            console.error('Monthly report error:', err);
            setStatus({ message: `Erro: ${err.message}`, type: 'error' });
        } finally {
            setIsGeneratingMonthly(false);
            setTimeout(() => setStatus({ message: '', type: '' }), 4000);
        }
    };

    // ---- Date range report flow ----
    const handleGenerateRangeReport = async () => {
        if (!rangeStartDate || !rangeEndDate) {
            setStatus({ message: 'Selecione a data inicial e a data final.', type: 'error' });
            setTimeout(() => setStatus({ message: '', type: '' }), 3000);
            return;
        }
        if (new Date(rangeStartDate) > new Date(rangeEndDate)) {
            setStatus({ message: 'A data inicial não pode ser maior que a data final.', type: 'error' });
            setTimeout(() => setStatus({ message: '', type: '' }), 3000);
            return;
        }

        setIsGeneratingRange(true);
        setStatus({ message: '⏳ Buscando relatórios do período...', type: 'info' });
        try {
            const data = await reportApi.getDateRangeReportData(rangeStartDate, rangeEndDate);

            if (data.length === 0) {
                const fmt = (s) => new Date(s).toLocaleDateString('pt-BR');
                setStatus({
                    message: `Nenhum relatório encontrado entre ${fmt(rangeStartDate)} e ${fmt(rangeEndDate)}.`,
                    type: 'error'
                });
                setIsGeneratingRange(false);
                setTimeout(() => setStatus({ message: '', type: '' }), 4000);
                return;
            }

            setStatus({ message: '📊 Gerando planilha...', type: 'info' });

            const buffer = await generateDateRangeReportXlsx(data, rangeStartDate, rangeEndDate);
            const blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const s = rangeStartDate.replace(/-/g, '');
            const e = rangeEndDate.replace(/-/g, '');
            saveAs(blob, `TUV_Report_${s}_to_${e}.xlsx`);

            setStatus({
                message: `✅ Relatório gerado com ${data.length} relatório(s).`,
                type: 'success'
            });
            setShowRangeModal(false);
        } catch (err) {
            console.error('Date range report error:', err);
            setStatus({ message: `Erro: ${err.message}`, type: 'error' });
        } finally {
            setIsGeneratingRange(false);
            setTimeout(() => setStatus({ message: '', type: '' }), 4000);
        }
    };

    return (
        <AuthGuard>
            <div className={styles.pageContainer}>
                {isModalOpen && (
                    <div className={styles.modalBackdrop} onClick={closeModal}>
                        <div
                            className={styles.formContainer}
                            style={{
                                maxWidth: '900px',
                                maxHeight: '90vh',
                                overflowY: 'auto',
                                position: 'relative',
                                borderRadius: '16px',
                                padding: '0',
                                boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div style={{
                                padding: '24px 32px',
                                borderBottom: '1px solid #e9ecef',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                borderRadius: '16px 16px 0 0',
                            }}>
                                <div>
                                    <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 700 }}>
                                        Novo Relatório TÜV AUSTRIA
                                    </h2>
                                    <p style={{ margin: '4px 0 0 0', opacity: 0.8, fontSize: '14px' }}>
                                        Selecione uma avaliação ou insira o ID manualmente
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    disabled={loading}
                                    style={{
                                        background: 'rgba(255,255,255,0.15)',
                                        border: 'none',
                                        color: 'white',
                                        width: '36px',
                                        height: '36px',
                                        borderRadius: '50%',
                                        fontSize: '24px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transition: 'all 0.2s',
                                        fontWeight: 300
                                    }}
                                    onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.25)'}
                                    onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.15)'}
                                >
                                    ×
                                </button>
                            </div>

                            <div style={{ padding: '24px 32px' }}>
                                {status.message && (
                                    <div style={{
                                        padding: '12px 16px',
                                        borderRadius: '10px',
                                        marginBottom: '20px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        background: status.type === 'error' ? '#fef3f2' :
                                            status.type === 'success' ? '#ecfdf5' : '#eff6ff',
                                        border: `1px solid ${status.type === 'error' ? '#fecaca' :
                                            status.type === 'success' ? '#a7f3d0' : '#bfdbfe'
                                            }`,
                                        color: status.type === 'error' ? '#dc2626' :
                                            status.type === 'success' ? '#065f46' : '#1e40af'
                                    }}>
                                        <span style={{ fontSize: '20px' }}>
                                            {status.type === 'error' ? '❌' : status.type === 'success' ? '✅' : 'ℹ️'}
                                        </span>
                                        <span style={{ fontSize: '14px', fontWeight: 500 }}>{status.message}</span>
                                    </div>
                                )}

                                <div style={{
                                    display: 'flex',
                                    gap: '0',
                                    background: '#f1f5f9',
                                    borderRadius: '12px',
                                    padding: '4px',
                                    marginBottom: '24px',
                                    position: 'relative'
                                }}>
                                    <button
                                        onClick={() => setSearchMode('list')}
                                        style={{
                                            flex: 1,
                                            padding: '10px 20px',
                                            border: 'none',
                                            borderRadius: '10px',
                                            background: searchMode === 'list' ? 'white' : 'transparent',
                                            color: searchMode === 'list' ? '#0d47a1' : '#64748b',
                                            cursor: 'pointer',
                                            fontWeight: 600,
                                            fontSize: '14px',
                                            transition: 'all 0.3s',
                                            boxShadow: searchMode === 'list' ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '8px'
                                        }}
                                    >
                                        <span>📋</span> Avaliações Recentes
                                    </button>
                                    <button
                                        onClick={() => setSearchMode('manual')}
                                        style={{
                                            flex: 1,
                                            padding: '10px 20px',
                                            border: 'none',
                                            borderRadius: '10px',
                                            background: searchMode === 'manual' ? 'white' : 'transparent',
                                            color: searchMode === 'manual' ? '#0d47a1' : '#64748b',
                                            cursor: 'pointer',
                                            fontWeight: 600,
                                            fontSize: '14px',
                                            transition: 'all 0.3s',
                                            boxShadow: searchMode === 'manual' ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '8px'
                                        }}
                                    >
                                        <span>🔍</span> Buscar por ID
                                    </button>
                                </div>

                                {searchMode === 'list' ? (
                                    <div style={{
                                        background: '#fafbfc',
                                        borderRadius: '12px',
                                        border: '1px solid #e9ecef',
                                        padding: '4px',
                                        maxHeight: '400px',
                                        overflow: 'auto'
                                    }}>
                                        <PendingEvaluationsList onSelect={handleSelectPending} />
                                    </div>
                                ) : (
                                    <div style={{
                                        background: '#fafbfc',
                                        borderRadius: '12px',
                                        border: '1px solid #e9ecef',
                                        padding: '24px'
                                    }}>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '14px',
                                            fontWeight: 600,
                                            color: '#1e293b',
                                            marginBottom: '8px'
                                        }}>
                                            ID da Avaliação (Evaluation ID)
                                        </label>
                                        <div style={{ display: 'flex', gap: '12px' }}>
                                            <input
                                                type="number"
                                                value={evaluationId}
                                                onChange={(e) => setEvaluationId(e.target.value)}
                                                placeholder="Ex: 12345"
                                                disabled={loading}
                                                style={{
                                                    flex: 1,
                                                    padding: '12px 16px',
                                                    border: '2px solid #e2e8f0',
                                                    borderRadius: '10px',
                                                    fontSize: '16px',
                                                    transition: 'all 0.2s',
                                                    outline: 'none',
                                                    background: 'white'
                                                }}
                                                onFocus={(e) => e.target.style.borderColor = '#0d47a1'}
                                                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                                            />
                                            <button
                                                type="button"
                                                onClick={handleFetchEvaluation}
                                                disabled={loading || !evaluationId}
                                                style={{
                                                    padding: '12px 28px',
                                                    background: loading || !evaluationId ? '#94a3b8' : '#0d47a1',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '10px',
                                                    fontSize: '14px',
                                                    fontWeight: 600,
                                                    cursor: loading || !evaluationId ? 'not-allowed' : 'pointer',
                                                    transition: 'all 0.2s',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    whiteSpace: 'nowrap'
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (!loading && evaluationId) {
                                                        e.target.style.background = '#1a237e';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (!loading && evaluationId) {
                                                        e.target.style.background = '#0d47a1';
                                                    }
                                                }}
                                            >
                                                {loading ? '⏳ Buscando...' : '🔍 Buscar'}
                                            </button>
                                        </div>
                                        <p style={{
                                            marginTop: '12px',
                                            fontSize: '12px',
                                            color: '#94a3b8'
                                        }}>
                                            Insira o ID da avaliação concluída no Checklist Fácil para extrair os dados automaticamente.
                                        </p>
                                    </div>
                                )}

                                {isDataLoaded && (
                                    <div style={{ marginTop: '24px' }}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '10px',
                                            marginBottom: '16px'
                                        }}>
                                            <span style={{ fontSize: '20px' }}>📋</span>
                                            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>
                                                Dados Extraídos da Avaliação
                                            </h3>
                                            <span style={{
                                                fontSize: '12px',
                                                padding: '2px 12px',
                                                borderRadius: '20px',
                                                background: '#dbeafe',
                                                color: '#1e40af',
                                                fontWeight: 500
                                            }}>
                                                {extractedData.perishableProducts?.length || 0} produtos
                                            </span>
                                            {extractedData.samplingGroupsCount > 0 && (
                                                <span style={{
                                                    fontSize: '12px',
                                                    padding: '2px 12px',
                                                    borderRadius: '20px',
                                                    background: '#e0e7ff',
                                                    color: '#4338ca',
                                                    fontWeight: 500
                                                }}>
                                                    {extractedData.samplingGroupsCount} grupos de amostragem
                                                </span>
                                            )}
                                            {extractedData.photoAttachments?.length > 0 && (
                                                <span style={{
                                                    fontSize: '12px',
                                                    padding: '2px 12px',
                                                    borderRadius: '20px',
                                                    background: '#dcfce7',
                                                    color: '#166534',
                                                    fontWeight: 500
                                                }}>
                                                    {extractedData.photoAttachments.length} fotos
                                                </span>
                                            )}
                                        </div>

                                        <div style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                                            gap: '12px',
                                            marginBottom: '16px'
                                        }}>
                                            {[
                                                { label: 'Projeto', value: extractedData.projectNo || '-' },
                                                { label: 'Cliente', value: extractedData.clientName || '-' },
                                                { label: 'Cliente (Export)', value: extractedData.clientNameExport || '-' },
                                                { label: 'País', value: extractedData.countryOfImport || '-' },
                                                { label: 'Inspetor', value: extractedData.inspectorName || '-' },
                                                { label: 'Data Inspeção', value: extractedData.dateOfInspection || '-' },
                                                { label: 'Data Relatório', value: extractedData.dateOfReport || '-' },
                                                {
                                                    label: 'Resultado', value: extractedData.inspectionResult || '-',
                                                    isResult: true
                                                }
                                            ].map((item, idx) => (
                                                <div key={idx} style={{
                                                    background: '#f8fafc',
                                                    padding: '12px 16px',
                                                    borderRadius: '10px',
                                                    border: '1px solid #e9ecef'
                                                }}>
                                                    <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                        {item.label}
                                                    </div>
                                                    <div style={{
                                                        fontSize: '14px',
                                                        fontWeight: 600,
                                                        color: '#1e293b',
                                                        marginTop: '4px',
                                                        ...(item.isResult && item.value !== '-' ? {
                                                            display: 'inline-block',
                                                            padding: '2px 12px',
                                                            borderRadius: '20px',
                                                            fontSize: '12px',
                                                            background: item.value.toLowerCase() === 'satisfactory' ? '#dcfce7' :
                                                                item.value.toLowerCase() === 'unsatisfactory' ? '#fee2e2' : '#fef3c7',
                                                            color: item.value.toLowerCase() === 'satisfactory' ? '#166534' :
                                                                item.value.toLowerCase() === 'unsatisfactory' ? '#991b1b' : '#92400e'
                                                        } : {})
                                                    }}>
                                                        {item.value}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        <div style={{
                                            display: 'flex',
                                            gap: '12px',
                                            justifyContent: 'flex-end',
                                            borderTop: '1px solid #e9ecef',
                                            paddingTop: '20px'
                                        }}>
                                            <button
                                                type="button"
                                                onClick={closeModal}
                                                disabled={loading}
                                                style={{
                                                    padding: '10px 24px',
                                                    background: 'transparent',
                                                    color: '#64748b',
                                                    border: '1px solid #e2e8f0',
                                                    borderRadius: '10px',
                                                    fontSize: '14px',
                                                    fontWeight: 500,
                                                    cursor: loading ? 'not-allowed' : 'pointer',
                                                    transition: 'all 0.2s'
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (!loading) {
                                                        e.target.style.background = '#f1f5f9';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (!loading) {
                                                        e.target.style.background = 'transparent';
                                                    }
                                                }}
                                            >
                                                Cancelar
                                            </button>
                                            <button
                                                type="button"
                                                onClick={generateReport}
                                                disabled={loading}
                                                style={{
                                                    padding: '10px 28px',
                                                    background: loading ? '#94a3b8' : 'linear-gradient(135deg, #0d47a1, #1a237e)',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '10px',
                                                    fontSize: '14px',
                                                    fontWeight: 600,
                                                    cursor: loading ? 'not-allowed' : 'pointer',
                                                    transition: 'all 0.2s',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    boxShadow: loading ? 'none' : '0 4px 12px rgba(13,71,161,0.3)'
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (!loading) {
                                                        e.target.style.transform = 'translateY(-2px)';
                                                        e.target.style.boxShadow = '0 6px 20px rgba(13,71,161,0.4)';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (!loading) {
                                                        e.target.style.transform = 'translateY(0)';
                                                        e.target.style.boxShadow = '0 4px 12px rgba(13,71,161,0.3)';
                                                    }
                                                }}
                                            >
                                                {loading ? '⏳ Gerando...' : '📊 Gerar Relatório'}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Download Format Modal */}
                {showDownloadModal && (
                    <div className={styles.modalBackdrop} onClick={() => !isDownloading && setShowDownloadModal(false)}>
                        <div
                            className={styles.formContainer}
                            style={{ maxWidth: '520px', padding: '24px 28px', borderRadius: '16px' }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: 700, color: '#1e293b' }}>
                                Choose Download Format
                            </h3>
                            <p style={{ margin: '0 0 20px 0', color: '#64748b', fontSize: '14px', lineHeight: 1.5 }}>
                                Select the format for the reports in the ZIP archive.
                                The ZIP will contain the folder{' '}
                                <code style={{ background: '#f1f5f9', padding: '1px 6px', borderRadius: '4px' }}>
                                    {reportToDownload?.reportFolder || 'Report_<INVOICE>_<CONTAINER>'}
                                </code>{' '}
                                with subfolders <strong>Inspection_Report</strong>,{' '}
                                <strong>Sampling_Data_Sheet</strong>, and <strong>Fotos</strong>.
                            </p>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                <button
                                    disabled={isDownloading}
                                    onClick={() => handleConfirmDownload('excel')}
                                    style={{
                                        padding: '12px 20px',
                                        background: isDownloading ? '#94a3b8' : 'linear-gradient(135deg, #0d47a1, #1a237e)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '10px',
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        cursor: isDownloading ? 'not-allowed' : 'pointer',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    📊 Download as Excel
                                </button>
                                <button
                                    disabled={isDownloading}
                                    onClick={() => handleConfirmDownload('pdf')}
                                    style={{
                                        padding: '12px 20px',
                                        background: isDownloading ? '#94a3b8' : 'linear-gradient(135deg, #b91c1c, #7f1d1d)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '10px',
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        cursor: isDownloading ? 'not-allowed' : 'pointer',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    📄 Download as PDF
                                </button>
                                {!isDownloading && (
                                    <button
                                        onClick={() => setShowDownloadModal(false)}
                                        style={{
                                            padding: '10px 20px',
                                            background: 'transparent',
                                            color: '#64748b',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '10px',
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            cursor: 'pointer',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        Cancel
                                    </button>
                                )}
                            </div>

                            {isDownloading && downloadProgress && (
                                <DownloadProgress
                                    progress={downloadProgress}
                                    format={downloadFormat}
                                />
                            )}
                        </div>
                    </div>
                )}

                {/* Download History Modal */}
                {showHistoryModal && reportToViewHistory && (
                    <DownloadHistoryModal
                        report={reportToViewHistory}
                        onClose={() => {
                            setShowHistoryModal(false);
                            setReportToViewHistory(null);
                        }}
                    />
                )}

                {/* Monthly Report Modal */}
                {showMonthlyModal && (
                    <div
                        className={styles.modalBackdrop}
                        onClick={() => !isGeneratingMonthly && setShowMonthlyModal(false)}
                    >
                        <div
                            className={styles.formContainer}
                            style={{ maxWidth: '460px', padding: '24px 28px', borderRadius: '16px' }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <h3 style={{
                                margin: '0 0 8px 0', fontSize: '20px',
                                fontWeight: 700, color: '#1e293b'
                            }}>
                                📅 Relatório Mensal
                            </h3>
                            <p style={{ margin: '0 0 20px 0', color: '#64748b', fontSize: '14px', lineHeight: 1.5 }}>
                                Selecione o mês e o ano. Será gerada uma planilha Excel com
                                todos os relatórios feitos nesse período, incluindo status de
                                download e informações principais.
                            </p>

                            <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
                                <div style={{ flex: 1 }}>
                                    <label style={{
                                        display: 'block', fontSize: '12px', fontWeight: 600,
                                        color: '#64748b', marginBottom: '6px',
                                        textTransform: 'uppercase', letterSpacing: '0.5px'
                                    }}>Mês</label>
                                    <select
                                        value={monthlyMonth}
                                        onChange={(e) => setMonthlyMonth(Number(e.target.value))}
                                        disabled={isGeneratingMonthly}
                                        style={{
                                            width: '100%', padding: '10px 12px',
                                            border: '2px solid #e2e8f0', borderRadius: '8px',
                                            fontSize: '14px', background: '#f8fafc', outline: 'none'
                                        }}
                                    >
                                        {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                                            <option key={m} value={m}>
                                                {new Date(2000, m - 1, 1)
                                                    .toLocaleString('pt-BR', { month: 'long' })}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label style={{
                                        display: 'block', fontSize: '12px', fontWeight: 600,
                                        color: '#64748b', marginBottom: '6px',
                                        textTransform: 'uppercase', letterSpacing: '0.5px'
                                    }}>Ano</label>
                                    <select
                                        value={monthlyYear}
                                        onChange={(e) => setMonthlyYear(Number(e.target.value))}
                                        disabled={isGeneratingMonthly}
                                        style={{
                                            width: '100%', padding: '10px 12px',
                                            border: '2px solid #e2e8f0', borderRadius: '8px',
                                            fontSize: '14px', background: '#f8fafc', outline: 'none'
                                        }}
                                    >
                                        {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i)
                                            .map(y => <option key={y} value={y}>{y}</option>)}
                                    </select>
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button
                                    disabled={isGeneratingMonthly}
                                    onClick={() => setShowMonthlyModal(false)}
                                    style={{
                                        flex: 1, padding: '10px 20px', background: 'transparent',
                                        color: '#64748b', border: '1px solid #e2e8f0',
                                        borderRadius: '10px', fontSize: '14px',
                                        cursor: isGeneratingMonthly ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    Cancelar
                                </button>
                                <button
                                    disabled={isGeneratingMonthly}
                                    onClick={handleGenerateMonthlyReport}
                                    style={{
                                        flex: 1, padding: '10px 20px',
                                        background: isGeneratingMonthly
                                            ? '#94a3b8'
                                            : 'linear-gradient(135deg, #b45309, #78350f)',
                                        color: 'white', border: 'none', borderRadius: '10px',
                                        fontSize: '14px', fontWeight: 600,
                                        cursor: isGeneratingMonthly ? 'not-allowed' : 'pointer',
                                        boxShadow: isGeneratingMonthly
                                            ? 'none'
                                            : '0 4px 12px rgba(180,83,9,0.3)'
                                    }}
                                >
                                    {isGeneratingMonthly ? '⏳ Gerando...' : '📊 Gerar Relatório'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Date Range Report Modal */}
                {showRangeModal && (
                    <div
                        className={styles.modalBackdrop}
                        onClick={() => !isGeneratingRange && setShowRangeModal(false)}
                    >
                        <div
                            className={styles.formContainer}
                            style={{ maxWidth: '460px', padding: '24px 28px', borderRadius: '16px' }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <h3 style={{
                                margin: '0 0 8px 0', fontSize: '20px',
                                fontWeight: 700, color: '#1e293b'
                            }}>
                                📆 Relatório por Período
                            </h3>
                            <p style={{ margin: '0 0 20px 0', color: '#64748b', fontSize: '14px', lineHeight: 1.5 }}>
                                Selecione a data inicial e a data final. Será gerada uma planilha
                                Excel com todos os relatórios feitos nesse intervalo, incluindo
                                status de download e informações principais.
                            </p>

                            <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
                                <div style={{ flex: 1 }}>
                                    <label style={{
                                        display: 'block', fontSize: '12px', fontWeight: 600,
                                        color: '#64748b', marginBottom: '6px',
                                        textTransform: 'uppercase', letterSpacing: '0.5px'
                                    }}>Data Inicial</label>
                                    <input
                                        type="date"
                                        value={rangeStartDate}
                                        onChange={(e) => setRangeStartDate(e.target.value)}
                                        disabled={isGeneratingRange}
                                        style={{
                                            width: '100%', padding: '10px 12px',
                                            border: '2px solid #e2e8f0', borderRadius: '8px',
                                            fontSize: '14px', background: '#f8fafc', outline: 'none'
                                        }}
                                    />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label style={{
                                        display: 'block', fontSize: '12px', fontWeight: 600,
                                        color: '#64748b', marginBottom: '6px',
                                        textTransform: 'uppercase', letterSpacing: '0.5px'
                                    }}>Data Final</label>
                                    <input
                                        type="date"
                                        value={rangeEndDate}
                                        onChange={(e) => setRangeEndDate(e.target.value)}
                                        disabled={isGeneratingRange}
                                        style={{
                                            width: '100%', padding: '10px 12px',
                                            border: '2px solid #e2e8f0', borderRadius: '8px',
                                            fontSize: '14px', background: '#f8fafc', outline: 'none'
                                        }}
                                    />
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button
                                    disabled={isGeneratingRange}
                                    onClick={() => setShowRangeModal(false)}
                                    style={{
                                        flex: 1, padding: '10px 20px', background: 'transparent',
                                        color: '#64748b', border: '1px solid #e2e8f0',
                                        borderRadius: '10px', fontSize: '14px',
                                        cursor: isGeneratingRange ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    Cancelar
                                </button>
                                <button
                                    disabled={isGeneratingRange}
                                    onClick={handleGenerateRangeReport}
                                    style={{
                                        flex: 1, padding: '10px 20px',
                                        background: isGeneratingRange
                                            ? '#94a3b8'
                                            : 'linear-gradient(135deg, #047857, #064e3b)',
                                        color: 'white', border: 'none', borderRadius: '10px',
                                        fontSize: '14px', fontWeight: 600,
                                        cursor: isGeneratingRange ? 'not-allowed' : 'pointer',
                                        boxShadow: isGeneratingRange
                                            ? 'none'
                                            : '0 4px 12px rgba(4,120,87,0.3)'
                                    }}
                                >
                                    {isGeneratingRange ? '⏳ Gerando...' : '📊 Gerar Relatório'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <div className={styles.header}>
                    <div>
                        <h1 className={styles.headerTitle}>Relatório de Inspeção TÜV AUSTRIA</h1>
                        <p style={{ color: '#666', marginTop: '4px' }}>Gerar relatório a partir de avaliação concluída no Checklist Fácil</p>
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                            onClick={() => setShowMonthlyModal(true)}
                            className={styles.addButton}
                            style={{ background: 'linear-gradient(135deg, #b45309, #78350f)' }}
                            disabled={loading}
                        >
                            📅 Relatório Mensal
                        </button>
                        <button
                            onClick={() => {
                                setRangeStartDate('');
                                setRangeEndDate('');
                                setShowRangeModal(true);
                            }}
                            className={styles.addButton}
                            style={{ background: 'linear-gradient(135deg, #047857, #064e3b)' }}
                            disabled={loading}
                        >
                            📆 Relatório por Período
                        </button>
                        <button onClick={openModal} className={styles.addButton} disabled={loading}>
                            + Novo Relatório
                        </button>
                    </div>
                </div>

                <div className={styles.reportsSection}>
                    <ReportsTable
                        reports={reports}
                        loading={reportsLoading}
                        onDownload={handleDownloadClick}
                        onDelete={handleDeleteReport}
                        onRefresh={loadReports}
                        onShowHistory={handleShowHistory}
                    />
                </div>
            </div>
        </AuthGuard>
    );
};

export default TUVReportPage;